import { ImportRefusedError } from "@/lib/import-refused-error"
import { isAxiosError } from "axios"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import type { ApiError } from "@/types/api"
import type { PermissionModule, PermissionOperation } from "@/lib/permissions"
import { formatIsoDateFr } from "@/lib/organisation-day"
import { CONTACT_EMAIL } from "@/lib/contact"

/** Refus de « terminée » d'une inscription, sans les chiffres du serveur. */
const ATTENDANCE_RATE_TOO_LOW_MESSAGE =
  "L'inscription ne peut pas être terminée : le taux de présence établi par l'émargement finalisé est inférieur au seuil requis. Finalisez l'émargement de la session."

// Known backend error messages mapped to user-friendly French actionable messages.
// Partial matches are checked case-insensitively against the extracted error detail.
const CONTRACT_NOT_SIGNED_MESSAGE =
  "Paiement refusé : le contrat n'est pas encore signé par toutes les parties. Il doit être conclu avant tout règlement de frais (article L6353-3 du Code du travail)."
/** Facture refusée pendant le délai : aucune somme exigible. */
const WITHDRAWAL_PERIOD_LAW =
  "(article L6353-6 du Code du travail ; pour un contrat conclu à distance ou hors établissement, Qualneo étend cette règle au délai de quatorze jours de l'article L221-18 du Code de la consommation)"
/**
 * Refus d'un paiement : L6353-6 interdit d'exiger une somme, pas d'enregistrer.
 * Le refus d'enregistrer est une règle de Qualneo.
 * Son extension aux quatorze jours aussi.
 */
const PAYMENT_DURING_WITHDRAWAL_RULE =
  "Aucune somme ne peut être exigée du stagiaire pendant le délai de dix jours de l'article L6353-5 (article L6353-6 du Code du travail) ; Qualneo n'enregistre aucun paiement avant la fin de ce délai, et l'applique aussi au délai de quatorze jours de l'article L221-18 du Code de la consommation pour un contrat conclu à distance ou hors établissement."
const WITHDRAWAL_PERIOD_RUNNING_FALLBACK = `Paiement refusé : le délai de rétractation du stagiaire court encore. ${PAYMENT_DURING_WITHDRAWAL_RULE} Si une somme a déjà été reçue pendant ce délai, rapprochez-vous du stagiaire pour la lui restituer.`
/** Rétractation refusée sur un contrat clos. */
const CLOSED_CONTRACT_WITHDRAWAL_MESSAGE =
  "Ce contrat est annulé, résilié ou expiré : la rétractation ne s'y enregistre plus."
/** Lettre postée après le délai, sans date fournie. */
const LETTER_LATE_FALLBACK =
  "La lettre de rétractation a été envoyée après le dernier jour du délai : la rétractation ne s'enregistre pas."
/** Un avoir numéroté reste dans la suite des numéros. */
const CREDIT_NOTE_NOT_DELETABLE_MESSAGE =
  "Cet avoir porte déjà un numéro et ne peut pas être supprimé, car la suite des numéros doit rester continue (art. 242 nonies A de l'annexe II au CGI) ; modifiez-le tant qu'il est en brouillon."
const DEPOSIT_CAP_EXCEEDED_FALLBACK =
  "Paiement refusé : le cumul des paiements dépasserait le plafond que Qualneo accepte à ce jour. À l'expiration du délai de rétractation, 30 % du prix convenu au plus peuvent être exigés ; le solde est échelonné au fur et à mesure du déroulement de la formation (article L6353-6 du Code du travail)."

/** Variable du modèle sans valeur, à la génération. */
const TEMPLATE_VARIABLE_MISSING_MESSAGE =
  "Le modèle utilise une information qui n'est pas renseignée pour cet élément : vérifiez l'écriture de la variable dans le modèle, ou complétez la fiche concernée, puis relancez la génération."

const ERROR_TRANSLATIONS: Array<{ pattern: string; message: string }> = [
  // Refus de lecture de la requête.
  { pattern: "invalid_request_body", message: "Le corps de la requête n'a pas le format attendu : vérifiez les champs envoyés, puis réessayez." },
  { pattern: "invalid_path_parameter", message: "Un identifiant de l'adresse n'a pas le format attendu : rouvrez la page depuis la liste." },
  { pattern: "invalid_query_parameter", message: "Un paramètre de la requête n'a pas le format attendu : effacez les filtres, puis réessayez." },
  { pattern: "request_body_too_large", message: "La requête dépasse la taille autorisée : réduisez le contenu envoyé." },
  { pattern: "unsupported_content_type", message: "Le type de contenu envoyé n'est pas pris en charge : envoyez du JSON." },
  { pattern: "rate_limited", message: "Trop de requêtes : réessayez dans un instant." },
  { pattern: "request_validation_failed", message: "Une valeur du formulaire n'est pas valide : vérifiez les champs, puis réessayez." },
  { pattern: "validation_error", message: "Une valeur saisie dépasse la longueur autorisée : raccourcissez-la, puis réessayez." },
  { pattern: "invalid_character", message: "Un texte saisi contient un caractère non autorisé (caractère nul) : retirez-le, puis réessayez." },
  // Rattachement d'un fichier : l'identifiant demande son type.
  { pattern: "entity_id_required", message: "Un type d'élément demande son identifiant : renseignez aussi l'élément auquel rattacher le fichier." },
  { pattern: "authentication_required", message: "Votre session a expiré ou n'existe pas : connectez-vous pour continuer." },
  { pattern: "access_token_invalid", message: "Le jeton d'accès est invalide, expiré ou révoqué : reconnectez-vous." },
  { pattern: "entity_type_required", message: "Un identifiant d'élément demande son type : renseignez aussi le type d'élément." },
  // Évaluation d'une inscription : le contact est celui du stagiaire.
  { pattern: "evaluation_registration_contact_mismatch", message: "Le contact de l'évaluation n'est pas celui de l'inscription : choisissez l'inscription du stagiaire, ou retirez le contact." },
  // Modèles d'enquête : code, nom, type.
  { pattern: "evaluation_template_code_immutable", message: "Le code d'un modèle ne change pas après sa création : laissez-le tel quel, ou créez un autre modèle." },
  { pattern: "evaluation_answer_too_long", message: "La réponse dépasse 10 000 caractères : raccourcissez-la." },
  { pattern: "survey_template_code_busy", message: "Le code du modèle est pris à l'instant par une autre création : réessayez." },
  { pattern: "survey_template_code_immutable", message: "Le code d'un modèle ne change pas après sa création : laissez-le tel quel, ou créez un autre modèle." },
  { pattern: "survey_template_code_invalid", message: "Le code ne contient que des lettres, des chiffres, _ et - (50 caractères au plus)." },
  { pattern: "survey_template_name_required", message: "Le nom du modèle compte de 1 à 255 caractères." },
  { pattern: "survey_template_type_unknown", message: "Ce type de modèle n'existe pas : choisissez un type de la liste." },
  { pattern: "survey_template_invalid", message: "Modèle refusé : la description compte 5 000 caractères au plus et la durée estimée va de 0 à 3 650 minutes." },
  // Formateurs de la session et module d'un créneau.
  { pattern: "session_instructor_module_invalid", message: "Un module confié à un formateur n'appartient pas au programme de cette session. Retirez-le ou choisissez un module du programme." },
  { pattern: "slot_module_invalid", message: "Ce module n'appartient pas au programme de la session. Choisissez un module du programme, ou laissez le champ vide." },
  // Connecteur MCP : consentement OAuth et chaque requête du serveur MCP.
  { pattern: "mcp_plan_required", message: `Le connecteur MCP est disponible à partir du plan Équipe. Les plans sont présentés sur qualneo.fr/tarifs ; pour changer le vôtre, écrivez à ${CONTACT_EMAIL}.` },
  // Invitation lifecycle (matched via Problem Details `code` field, see below).
  { pattern: "email_already_verified", message: "Cette adresse e-mail est déjà vérifiée : connectez-vous avec votre mot de passe." },
  { pattern: "invitation_pending", message: "Une invitation à rejoindre une organisation existe déjà pour cet email. Vérifiez votre boîte mail (et vos spams), ou demandez à votre administrateur de la renvoyer." },
  // Évaluations : note saisie, réponses du stagiaire
  { pattern: "evaluation_score_invalid", message: "La note doit être un nombre de 0 à 100, avec 2 décimales au plus." },
  { pattern: "evaluation_closed", message: "Cette évaluation est close : elle n'accepte plus de réponse." },
  { pattern: "evaluation_not_editable", message: "Cette évaluation n'accepte plus de réponse de l'organisme : seule une évaluation en brouillon ou planifiée en reçoit. Une fois envoyée, le stagiaire répond depuis son portail." },
  { pattern: "evaluation_deadline_invalid", message: "L'échéance de l'évaluation doit être à venir, et après sa date de programmation : choisissez une date plus lointaine." },
  { pattern: "evaluation_answers_invalid", message: "Certaines réponses manquent ou ne correspondent pas à la question. Complétez les questions obligatoires puis renvoyez vos réponses." },
  { pattern: "evaluation_question_invalid", message: "Question refusée : texte de 1 à 2 000 caractères, de 2 à 50 choix distincts pour une question à choix, aucun choix pour une question libre, pondération de 0 à 100. Le dépôt de fichier n'est pas proposé au stagiaire." },
  { pattern: "evaluation_question_has_answers", message: "Cette question a déjà reçu des réponses : son type et ses choix restent tels quels, et elle reste dans le modèle. Son texte peut encore changer." },
  { pattern: "evaluation_template_invalid", message: "Modèle refusé : le nom (255 caractères au plus) et le code (50 caractères au plus) sont obligatoires, les textes font 5 000 caractères au plus, la durée et le délai vont de 0 à 3 650, les relances de 0 à 10 et le seuil de réussite de 0 à 100." },
  // QR de signature et questionnaire public.
  // L'organisme se nomme avec un contact (RGPD art. 13).
  { pattern: "organization_public_contact_required", message: "Renseignez l'e-mail ou le téléphone de contact public de votre organisme dans Paramètres › Organisation. La personne qui signe ou qui répond doit savoir qui contacter pour ses données." },
  // Questionnaire de satisfaction public (/survey/:token)
  { pattern: "survey_expired", message: "Ce questionnaire n'est plus disponible : il a expiré ou a été annulé. Contactez votre organisme de formation si vous souhaitez encore répondre." },
  { pattern: "survey_already_completed", message: "Vous avez déjà répondu à ce questionnaire : vos réponses sont enregistrées. Merci." },
  { pattern: "survey_template_missing", message: "Aucun questionnaire de satisfaction actif ne correspond à cet envoi : créez ou réactivez un modèle d'enquête, puis relancez la tâche." },
  { pattern: "survey_answers_invalid", message: "Certaines réponses manquent ou ne correspondent pas à la question. Complétez les questions obligatoires puis renvoyez vos réponses." },
  // Suppression d'une question de modèle d'enquête.
  { pattern: "survey_question_has_answers", message: "Cette question a déjà reçu des réponses : la supprimer les effacerait. Elle reste dans le questionnaire." },
  { pattern: "survey_question_dependency_invalid", message: "Une question ne peut dépendre que d'une autre question du même questionnaire : choisissez une question de ce questionnaire." },
  { pattern: "survey_question_has_dependents", message: "D'autres questions s'affichent selon la réponse à celle-ci. Retirez d'abord cette condition de ces questions." },
  // Modèle d'enquête encore désigné : suppression refusée.
  { pattern: "program_needs_template_invalid", message: "Ce modèle ne peut pas servir au recueil des besoins du programme : choisissez un modèle de recueil des besoins de votre organisme, non supprimé." },
  { pattern: "survey_template_in_use", message: "Ce modèle sert encore au recueil des besoins d'un programme ou d'un devis en cours, ou c'est le modèle par défaut (Paramètres > Recueil des besoins) : il reste en place." },
  { pattern: "survey_assignment_target_invalid", message: "Ce destinataire n'existe pas dans votre organisme : choisissez un contact non supprimé." },
  { pattern: "survey_assignment_template_not_sendable", message: "Ce modèle n'est pas proposé à l'envoi : seul un modèle actif et publié part en invitation. Publiez-le, ou choisissez un modèle publié." },
  { pattern: "survey_assignment_target_stakeholder_mismatch", message: "Ce modèle recueille l'appréciation d'une autre partie prenante : choisissez un destinataire qui en relève (par exemple un formateur pour l'appréciation des formateurs)." },
  { pattern: "survey_template_auto_send_recipient", message: "L'envoi automatique part aux stagiaires inscrits : il ne vaut que pour un modèle qui leur est destiné. Envoyez ce modèle par invitation." },
  { pattern: "evaluation_target_stakeholder_mismatch", message: "Le stagiaire ne répond pas pour son employeur ni son financeur : rattachez cette évaluation à la session et au contact de l'employeur ou du financeur, sans inscription." },
  { pattern: "survey_template_auto_send_trigger_required", message: "L'envoi automatique exige un déclencheur : la fin de session, le début de session ou l'inscription confirmée." },
  { pattern: "survey_template_stakeholder_category_fixed", message: "Partie prenante refusée : seul un modèle personnalisé choisit la sienne, dans la liste à jour des parties prenantes." },
  // Supports de session
  { pattern: "session_document_invalid", message: "Support refusé : envoyez un fichier PDF, JPEG ou PNG non vide, avec un titre de 255 caractères au plus." },
  { pattern: "session_document_not_found", message: "Ce support n'est plus disponible : il a été retiré de la session." },
  { pattern: "session_document_reserved", message: "Ce fichier est un support de session : il se modifie et se retire depuis la fiche session, onglet Supports." },
  // Fiche de poste : seule sa génération la pose.
  { pattern: "job_description_locked", message: "La fiche de poste est en signature ou signée : révoquez la signature pour modifier ses missions, compétences ou fonctions Qualiopi." },
  { pattern: "job_description_reference_unknown", message: "Une mission, une compétence ou une fonction choisie n'existe pas dans votre organisme : rechargez la page puis recommencez." },
  { pattern: "mission_system_not_deletable", message: "Une mission système ne se supprime pas : seules les missions créées par votre organisme se suppriment." },
  { pattern: "staff_evaluation_staff_invalid", message: "Ce membre du personnel n'existe pas ou a quitté votre organisme : choisissez-en un dans la liste." },
  { pattern: "staff_evaluation_job_type_invalid", message: "Ce type de poste n'existe pas pour votre organisme : choisissez-en un dans la liste." },
  { pattern: "job_description_file_reserved", message: "La fiche de poste se génère depuis la fiche du collaborateur, onglet Fiche de poste : un document ne peut pas être classé comme fiche de poste." },
  // Documents de formateur : type connu, fichier déposé pour lui.
  { pattern: "instructor_document_type_unknown", message: "Type de document inconnu : choisissez un type dans la liste." },
  { pattern: "instructor_document_file_invalid", message: "Ce fichier ne peut pas être rattaché à ce formateur : ajoutez le document depuis sa fiche, onglet Documents." },
  { pattern: "instructor_document_reserved", message: "Ce fichier est un document de formateur : il se modifie et se retire depuis la fiche du formateur, onglet Documents." },
  // Pièces de sous-traitant : fichier déposé depuis sa fiche.
  { pattern: "vendor_document_file_invalid", message: "Ce fichier ne peut pas être rattaché à ce sous-traitant : ajoutez la pièce depuis sa fiche, onglet « Adéquation au référentiel (indicateur 27) »." },
  // Entretiens professionnels : type de la liste.
  { pattern: "interview_type_unknown", message: "Type d'entretien inconnu : choisissez entretien professionnel, annuel ou spécifique." },
  // Membre de l'équipe : retirer son accès avant de le désactiver.
  { pattern: "staff_has_active_account", message: "Ce membre a encore un accès à Qualneo : retirez-le d'abord dans Paramètres › Équipe, ce qui ferme aussi ses sessions, puis désactivez le membre." },
  // Modèles d'email : le catalogue système est commun à tous les organismes
  { pattern: "email_template_code_reserved", message: "Ce code appartient à un modèle système. Pour l'adapter, cliquez sur « Personnaliser » sur ce modèle ; pour un nouveau modèle, choisissez un autre code." },
  { pattern: "email_template_code_already_exists", message: "Un de vos modèles utilise déjà ce code. Modifiez ce modèle ou choisissez un autre code." },
  { pattern: "email_template_no_system_default", message: "Ce modèle a été créé par votre organisme : il n'a pas de version système à restaurer." },
  { pattern: "email_template_auth_not_customizable", message: "Ce modèle contient un lien de connexion : il est toujours envoyé dans sa version Qualneo et ne se personnalise pas." },
  { pattern: "email_template_partial_forbidden", message: "Le modèle utilise un partial ou un décorateur ({{> …}}, {{#*inline …}}) : les modèles d'e-mails n'en acceptent pas, écrivez le contenu en entier." },
  { pattern: "email_template_nesting_too_deep", message: "Le modèle imbrique trop de blocs ou de sous-expressions : simplifiez-le." },
  { pattern: "email_template_source_too_long", message: "Le modèle dépasse 100 000 caractères : raccourcissez-le." },
  { pattern: "email_template_render_too_large", message: "Le modèle produit plus de 5 Mo avec ces données : réduisez les répétitions ou les données." },
  { pattern: "email_template_render_budget_exceeded", message: "Le modèle demande trop de calcul avec ces données : réduisez les boucles {{#each}} imbriquées." },
  { pattern: "email_template_render_busy", message: "Le rendu des modèles est saturé : réessayez dans quelques secondes." },
  { pattern: "email_template_preview_rate_limited", message: "Trop d'aperçus d'affilée pour votre organisme : patientez une seconde avant le suivant." },
  { pattern: "email_template_variables_too_large", message: "Les variables de l'aperçu dépassent 256 Ko : réduisez-les." },
  { pattern: "email_template_helper_unknown", message: "Le modèle appelle une fonction qui n'existe pas : un modèle d'e-mail n'appelle que les fonctions de Handlebars ({{#if}}, {{#each}}, {{#unless}}, {{#with}}, eq, lookup…)." },
  // Modèles de documents : refus nommés par le serveur.
  { pattern: "document_template_engine_unsupported", message: "Les modèles de documents se rendent en Handlebars : seul le moteur « handlebars » est accepté. Écrivez les variables entre doubles accolades, par exemple {{nom}}." },
  { pattern: "document_template_code_already_exists", message: "Un de vos modèles de documents porte déjà ce code : choisissez un autre code, ou modifiez ce modèle." },
  { pattern: "document_template_type_invalid", message: "Ce type de document n'existe pas : rechargez la page, puis choisissez un type dans la liste." },
  { pattern: "document_template_invalid", message: "Modèle refusé : code de 1 à 50 caractères, nom de 1 à 255 caractères, contenu HTML non vide, format A4, A3, Letter ou Legal, orientation portrait ou paysage. Contenu, en-tête et pied de page de 100 000 caractères au plus, styles de 20 000, description de 2 000, 200 variables de 100 caractères ; marges en mm, cm, in ou px, par exemple 15mm ; un nombre seul compte en millimètres." },
  { pattern: "document_template_syntax_invalid", message: "Le modèle n'est pas un Handlebars valide : chaque {{ doit avoir ses }}, et chaque bloc {{#…}} sa fermeture {{/…}}." },
  { pattern: "document_template_helper_unknown", message: "Le modèle appelle une fonction qui n'existe pas : les fonctions acceptées sont format_date, format_currency et celles de Handlebars (if, each, unless, with, eq, lookup…)." },
  { pattern: "document_template_partial_forbidden", message: "Le modèle utilise un partial ou un décorateur ({{> …}}, {{#*inline …}}) : les modèles n'en acceptent pas, écrivez le contenu en entier." },
  { pattern: "document_template_nesting_too_deep", message: "Le modèle imbrique trop de blocs ou de sous-expressions : simplifiez-le." },
  { pattern: "document_template_source_too_long", message: "Le modèle dépasse 100 000 caractères : raccourcissez-le." },
  { pattern: "document_template_render_too_large", message: "Le modèle produit plus de 5 Mo avec ces données : réduisez les répétitions ou les données." },
  { pattern: "document_template_render_budget_exceeded", message: "Le modèle demande trop de calcul avec ces données : réduisez les boucles {{#each}} imbriquées." },
  { pattern: "document_template_render_busy", message: "Le rendu des modèles est saturé : réessayez dans quelques secondes." },
  { pattern: "document_template_preview_rate_limited", message: "Trop d'aperçus d'affilée pour votre organisme : patientez une seconde avant le suivant." },
  { pattern: "document_template_context_too_large", message: "Les données de l'aperçu dépassent 256 Ko : réduisez-les." },
  { pattern: "document_template_render_failed", message: "Le modèle ne se rend pas avec les données fournies : vérifiez ses variables et ses blocs." },
  { pattern: "document_template_serves_card", message: "Ce modèle génère une carte de Documents › Générer, et aucun autre modèle actif ne porte son code : le désactiver laisserait cette carte sans modèle." },
  { pattern: "document_template_system_read_only", message: "Ce modèle est fourni par Qualneo (certificat de réalisation, conventions, attestations) : il ne se modifie ni ne se désactive, quel que soit le rôle." },
  // Plafond du taux journalier formateur (Paramètres › Organisation).
  { pattern: "instructor_rate_cap_invalid", message: "Le plafond journalier doit être supérieur à 0 et au plus 100 000 € HT, avec deux décimales au plus." },
  { pattern: "instructor_hours_per_day_invalid", message: "La journée doit compter plus de 0 et au plus 24 heures, avec deux décimales au plus." },
  // Règles OPCO système : communes à tous les organismes.
  { pattern: "opco_rule_system_read_only", message: "Cette règle est une règle système, commune à tous les organismes : elle ne se modifie pas et ne se retire pas. Ajoutez une règle de votre organisme pour compléter le contrôle." },
  // Registration
  { pattern: "registration not found", message: "Inscription introuvable. Elle a peut-être été supprimée." },
  { pattern: "session is full", message: "Cette session est complète. Aucune place disponible." },
  { pattern: "already registered", message: "Ce participant est déjà inscrit à cette session." },
  { pattern: "registration is not pending", message: "Cette inscription ne peut plus être modifiée car elle n'est plus en attente." },
  // Authentication
  { pattern: "authentication required", message: "Votre session a expiré. Veuillez vous reconnecter." },
  { pattern: "invalid credentials", message: "Email ou mot de passe incorrect." },
  { pattern: "token expired", message: "Votre session a expiré. Veuillez vous reconnecter." },
  { pattern: "unauthorized", message: "Vous n'avez pas les droits pour effectuer cette action." },
  // Quotes
  { pattern: "quote already accepted", message: "Ce devis a déjà été accepté." },
  { pattern: "quote not in draft", message: "Ce devis ne peut plus être modifié car il n'est plus en brouillon." },
  { pattern: "quote has expired", message: "Ce devis a expiré. Créez-en un nouveau." },
  // Invoices
  { pattern: "invoice is locked", message: "Cette facture est verrouillée et ne peut plus être modifiée." },
  { pattern: "invoice already paid", message: "Cette facture est déjà entièrement payée." },
  { pattern: "payment exceeds", message: "Le montant du paiement dépasse le reste à payer." },
  // Contracts
  { pattern: "contract not in draft", message: "Ce contrat ne peut plus être modifié car il n'est plus en brouillon." },
  { pattern: "contract already signed", message: "Ce contrat a déjà été signé." },
  // Signature manuelle d'une partie sans représentant joignable (422).
  { pattern: "party_representative_email_required", message: "Renseignez l'email du représentant de cette partie avant d'enregistrer sa signature." },
  // Signature unique par partie (409), finalisation après la dernière (422).
  { pattern: "contract_party_already_signed", message: "Cette partie a déjà signé : sa signature est enregistrée et sa date ne change pas." },
  { pattern: "contract_party_not_signed", message: "Chaque partie doit avoir signé avant la finalisation : enregistrez la signature manquante dans l'onglet Signatures." },
  { pattern: "contract_not_finalizable", message: "Ce contrat n'est plus en cours de signature (terminé, rétracté, annulé, résilié ou expiré) : il ne se finalise pas." },
  // Publication de programme (gardes Qualiopi — filet si le bouton jaugé est contourné)
  { pattern: "assessment_methods_required", message: "Renseignez les modalités d'évaluation avant de publier le programme (Qualiopi Ind. 1 et 11)." },
  { pattern: "no_prerequisites_conflict", message: "« Aucun prérequis » est déclaré alors que des prérequis sont renseignés. Retirez la liste ou décochez la déclaration (Qualiopi, indicateur 1)." },
  { pattern: "prerequisites_required", message: "Ajoutez au moins un prérequis ou déclarez « aucun prérequis » avant de publier (Qualiopi, indicateur 1)." },
  { pattern: "success_criteria_required", message: "Ajoutez au moins un critère de réussite avant de publier : il sert à évaluer l'atteinte des objectifs (Qualiopi Ind. 11)." },
  { pattern: "financing_modalities_required", message: "Précisez les modalités de financement dans l'onglet Tarifs avant de publier le programme : l'indicateur 1 impose d'informer le public sur ce point." },
  { pattern: "financing_type_unknown", message: "Ce type de financement n'existe pas ou plus pour votre organisme : choisissez-en un dans la liste, puis enregistrez de nouveau." },
  { pattern: "certification_type_required", message: "Indiquez le type de reconnaissance de la formation délivrée (par exemple certification RNCP ou RS, CQP, ou aucune) dans l'onglet Détails avant de publier le programme : l'indicateur 1 impose d'informer le public sur ce point." },
  { pattern: "certification_type_not_a_certification", message: "« Aucune reconnaissance » ne désigne pas une certification : choisissez le type de la certification du formateur (RNCP, RS, CQP ou interne)." },
  { pattern: "certification_type_unknown", message: "Ce type de certification n'existe pas pour votre organisme : choisissez-en un dans la liste." },
  // Pièces de compétence du personnel (diplômes, certifications) : dates et lien.
  { pattern: "expiry_date_before_issue_date", message: "La date d'expiration ne peut pas précéder la date d'obtention : corrigez l'une des deux dates." },
  { pattern: "link_not_http", message: "Le lien doit être une adresse web commençant par http:// ou https://." },
  // Génération de documents (certificat de réalisation : heures tirées de l'émargement)
  { pattern: "no_finalized_attendance", message: "Aucune feuille d'émargement finalisée pour cette session : finalisez l'émargement avant de générer ce document (certificat de réalisation ou attestation d'assiduité)." },
  // Dossier de preuves : scan d'émargement papier, pièces externes, export ZIP.
  // Codes portés par le champ `code` (422/413/415/429/410) ou en préfixe du
  // `detail` (400/404) : les deux formes passent par cette table.
  { pattern: "attendance_scan_required", message: "Cette feuille porte des émargements sur papier : joignez le scan de la feuille signée avant de la finaliser." },
  // Envoi de scan refusé : la feuille finalisée a déjà le sien (une feuille
  // finalisée sans scan en accepte un, de rattrapage).
  { pattern: "attendance_sheet_finalized", message: "Cette feuille d'émargement finalisée a déjà son scan : il ne se remplace pas." },
  { pattern: "attendance_records_locked", message: "La feuille d'émargement est finalisée : ses présences ne peuvent plus être saisies ni modifiées." },
  { pattern: "session_status_via_transition", message: "Le statut d'une session se change par « Changer le statut » de sa fiche, qui contrôle le passage et enregistre la fin réelle." },
  { pattern: "session_actual_end_out_of_range", message: "La fin réelle de la session ne peut pas être postérieure à aujourd'hui, ni antérieure à la dernière feuille d'émargement finalisée de la session." },
  { pattern: "session_dates_exclude_finalized_sheets", message: "Les dates de la session doivent couvrir ses feuilles d'émargement finalisées : le début au plus tard le jour de la première, la fin au plus tôt le jour de la dernière." },
  { pattern: "session_status_unknown", message: "Statut inconnu : une session se crée en brouillon ou planifiée." },
  { pattern: "attendance_sheet_after_actual_end", message: "La feuille est datée après la fin réelle de la session : reportez la fin réelle dans la fiche session (« Modifier »)." },
  { pattern: "attendance_sheet_in_future", message: "Une feuille datée d'un jour à venir ne se finalise pas : finalisez-la le jour de la séance, ou après." },
  // Équipe : un administrateur ne retire pas son propre accès.
  // Clés étrangères : la ligne choisie doit appartenir à l'organisme.
  { pattern: "contact_account_invalid", message: "Ce compte n'existe pas dans votre organisme : choisissez un compte dans la liste." },
  { pattern: "session_program_invalid", message: "Ce programme n'existe pas dans votre organisme : choisissez un programme dans la liste." },
  { pattern: "invoice_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez le contact de facturation dans la liste." },
  { pattern: "invoice_quote_invalid", message: "Ce devis n'existe pas dans votre organisme : choisissez un devis dans la liste." },
  { pattern: "contract_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez le contact du contrat dans la liste." },
  { pattern: "contract_session_invalid", message: "Cette session n'existe pas dans votre organisme : choisissez une session dans la liste." },
  { pattern: "contract_registration_invalid", message: "Cette inscription n'existe pas dans votre organisme : choisissez une inscription dans la liste." },
  { pattern: "contract_party_account_invalid", message: "Le compte de cette partie n'existe pas dans votre organisme : choisissez un compte dans la liste." },
  { pattern: "contract_party_contact_invalid", message: "Le contact de cette partie n'existe pas dans votre organisme : choisissez un contact dans la liste." },
  { pattern: "opportunity_account_invalid", message: "Ce compte n'existe pas dans votre organisme : choisissez le compte de l'opportunité dans la liste." },
  { pattern: "opportunity_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez l'interlocuteur de l'opportunité dans la liste." },
  { pattern: "opportunity_program_invalid", message: "Ce programme n'existe pas dans votre organisme : choisissez le programme visé par l'opportunité." },
  { pattern: "opportunity_assigned_to_invalid", message: "Ce responsable n'est pas membre de votre organisme : choisissez un membre de l'équipe." },
  { pattern: "quote_needs_program_invalid", message: "Ce programme n'existe pas dans votre organisme : choisissez le programme concerné par ce besoin." },
  { pattern: "quote_needs_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez la personne interrogée dans la liste." },
  { pattern: "task_assigned_to_invalid", message: "Cette personne n'est pas membre de votre organisme : choisissez un membre de l'équipe." },
  { pattern: "session_type_invalid", message: "Ce type de session n'existe pas dans votre organisme : choisissez un type de la liste." },
  { pattern: "session_venue_invalid", message: "Ce lieu n'existe pas dans votre organisme : choisissez un lieu de la liste, ou créez-le dans Qualiopi › Lieux de formation." },
  { pattern: "attendance_sheet_instructor_invalid", message: "Ce formateur n'existe pas dans votre organisme : choisissez un contact de votre organisme." },
  { pattern: "task_parent_task_invalid", message: "Cette tâche parente n'existe pas dans votre organisme : choisissez une tâche de la liste." },
  { pattern: "task_related_entity_invalid", message: "L'élément lié à cette tâche n'existe pas dans votre organisme, ou son type n'est pas reconnu." },
  { pattern: "invoice_payment_terms_invalid", message: "Ces conditions de paiement ne sont pas disponibles pour cette facture : choisissez-en dans la liste." },
  { pattern: "quote_payment_terms_invalid", message: "Ces conditions de paiement ne sont pas disponibles pour ce devis : choisissez-en dans la liste." },
  { pattern: "conversation_related_entity_invalid", message: "L'élément lié à cette conversation n'existe pas dans votre organisme, ou son type n'est pas reconnu." },
  { pattern: "conversation_participant_invalid", message: "Un des participants choisis n'est pas membre de votre organisme : retirez-le de la conversation." },
  { pattern: "quote_line_session_invalid", message: "La session d'une ligne du devis n'existe pas dans votre organisme : choisissez une session de la liste." },
  { pattern: "quote_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez le contact du devis dans la liste." },
  { pattern: "quote_template_program_invalid", message: "Ce programme n'existe pas dans votre organisme : choisissez le programme du modèle de devis." },
  { pattern: "amendment_line_program_invalid", message: "Le programme de cette ligne d'avenant n'existe pas dans votre organisme." },
  { pattern: "amendment_line_session_invalid", message: "La session de cette ligne d'avenant n'existe pas dans votre organisme : choisissez-en une de la liste." },
  { pattern: "account_assigned_to_invalid", message: "Ce responsable de compte n'est pas membre de votre organisme : choisissez un membre de l'équipe." },
  { pattern: "enrollment_session_invalid", message: "Cette session n'existe pas dans votre organisme : choisissez la session de l'inscription dans la liste." },
  { pattern: "enrollment_contact_invalid", message: "Ce stagiaire n'existe pas dans votre organisme : choisissez-le parmi vos contacts." },
  { pattern: "registration_session_invalid", message: "Cette session n'existe pas dans votre organisme : choisissez la session de l'inscription dans la liste." },
  { pattern: "registration_contact_invalid", message: "Ce stagiaire n'existe pas dans votre organisme : choisissez-le parmi vos contacts." },
  { pattern: "vae_journey_session_invalid", message: "Cette session n'existe pas dans votre organisme : choisissez la session du parcours VAE." },
  { pattern: "program_competency_competency_invalid", message: "Cette compétence n'existe pas dans votre organisme : choisissez-la dans Formation › Référentiels de compétences." },
  { pattern: "jury_venue_invalid", message: "Ce lieu n'existe pas dans votre organisme : choisissez le lieu du jury dans Qualiopi › Lieux de formation." },
  { pattern: "slot_venue_invalid", message: "Ce lieu n'existe pas dans votre organisme : choisissez le lieu du créneau dans la liste." },
  { pattern: "venue_evaluation_session_invalid", message: "La session évaluée n'existe pas dans votre organisme : choisissez une session de la liste." },
  { pattern: "exam_session_examiner_invalid", message: "Cet examinateur n'existe pas dans votre organisme : choisissez-le parmi vos contacts." },
  { pattern: "exam_candidate_registration_invalid", message: "Cette inscription n'existe pas dans votre organisme ou ne concerne pas ce stagiaire : choisissez une inscription du candidat." },
  { pattern: "needs_assessment_contact_invalid", message: "Ce contact n'existe pas dans votre organisme : choisissez la personne de l'analyse des besoins." },
  { pattern: "needs_assessment_account_invalid", message: "Ce compte n'existe pas dans votre organisme : choisissez l'entreprise de l'analyse des besoins." },
  { pattern: "needs_assessment_assigned_to_invalid", message: "Ce responsable de l'analyse des besoins n'est pas membre de votre organisme." },
  { pattern: "needs_assessment_opportunity_invalid", message: "Cette opportunité n'existe pas dans votre organisme : choisissez-la dans CRM › Opportunités." },
  { pattern: "needs_assessment_status_invalid", message: "Ce statut d'analyse des besoins n'est pas disponible dans votre organisme." },
  { pattern: "session_instructor_invalid", message: "Un des formateurs choisis n'existe pas dans votre organisme : choisissez-les dans RH › Formateurs." },
  { pattern: "availability_slot_time_order", message: "L'heure de fin du créneau doit suivre son heure de début." },
  { pattern: "availability_slot_date_required", message: "Un créneau « date unique » demande sa date." },
  { pattern: "availability_slot_day_mismatch", message: "Le jour du créneau ne correspond pas à sa date : choisissez le jour de la date, ou une autre date." },
  { pattern: "instructor_skill_proof_document_invalid", message: "Ce justificatif n'existe pas dans votre organisme : déposez-le depuis la fiche du formateur." },
  { pattern: "self_verification_instructor_invalid", message: "Ce formateur n'existe pas dans votre organisme : choisissez-le dans RH › Formateurs avant l'auto-évaluation." },
  // Données de santé : consentement explicite du stagiaire (RGPD art. 9(2)(a)).
  { pattern: "health_data_consent_required", message: "Le stagiaire n'a pas donné son consentement aux données de santé. Enregistrez-le d'abord dans sa fiche contact, onglet Accessibilité." },
  { pattern: "health_data_consent_channel_unknown", message: "Choisissez dans la liste le canal par lequel le consentement a été recueilli." },
  { pattern: "health_data_consent_not_found", message: "Aucun consentement en cours pour ce stagiaire : il n'y a rien à retirer." },
  // Orientations, liste des stagiaires à besoins : lecteurs du diagnostic seulement.
  { pattern: "health_consent_text_incomplete", message: "Le texte du consentement est incomplet : l'organisme doit renseigner son identité (nom, adresse, SIRET, e-mail de contact), sa nature juridique et son choix sur le délégué à la protection des données dans Paramètres > Organisation." },
  { pattern: "dpo_contact_required", message: "Indiquez les coordonnées du délégué à la protection des données désigné." },
  { pattern: "dpo_contact_too_long", message: "Les coordonnées du délégué à la protection des données sont limitées à 320 caractères." },
  { pattern: "legal_nature_not_found", message: "Choisissez la nature juridique de l'organisme dans la liste : organisme privé ou public." },
  { pattern: "health_data_read_forbidden", message: "Accès restreint : seuls les administrateurs et les référents handicap de l'organisme (rôle ou référent désigné dans les paramètres) peuvent consulter ces informations." },
  { pattern: "disability_types_too_many", message: "20 types de handicap au plus par déclaration : retirez ceux qui ne vous concernent pas." },
  { pattern: "disability_type_unknown", message: "Ce type de handicap n'est pas proposé par votre organisme : choisissez-le dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "disability_referral_status_invalid", message: "Statut d'orientation inconnu : choisissez « À contacter », « Contacté », « En cours », « Terminée » ou « Annulée »." },
  { pattern: "enrollment_adaptation_type_invalid", message: "Type d'adaptation inconnu : choisissez handicap, langue, horaires, contenu ou mixte." },
  { pattern: "disability_referral_organization_invalid", message: "Cet organisme d'accompagnement n'est pas disponible dans votre organisme : choisissez-en un dans la liste." },
  { pattern: "jury_member_role_invalid", message: "Ce rôle de jury n'est pas disponible dans votre organisme : choisissez un rôle de la liste." },
  { pattern: "opco_file_registration_invalid", message: "Une des inscriptions choisies n'existe pas dans votre organisme : retirez-la du dossier OPCO." },
  { pattern: "jury_deliberation_registration_invalid", message: "Cette inscription n'est pas candidate de la session de ce jury : choisissez un candidat du jury." },
  { pattern: "jury_deliberation_text_too_long", message: "Les notes de délibération ou les conditions dépassent 5000 caractères : raccourcissez-les." },
  { pattern: "value_too_long", message: "Une valeur saisie dépasse la longueur autorisée : raccourcissez-la." },
  { pattern: "value_out_of_range", message: "Une valeur numérique dépasse la plage autorisée : corrigez le montant ou la quantité." },
  { pattern: "watch_source_responsible_invalid", message: "Ce responsable n'est pas membre de votre organisme : choisissez-le dans la liste de l'équipe." },
  { pattern: "watch_attachment_file_invalid", message: "Ce fichier n'existe pas dans votre organisme : déposez-le à nouveau." },
  { pattern: "venue_evaluation_criteria_invalid", message: "Un critère ou un point de contrôle n'existe pas dans votre organisme : rechargez la grille d'évaluation." },
  { pattern: "cannot_delete_self", message: "Vous ne pouvez pas retirer votre propre accès : demandez à un autre administrateur de le faire depuis Paramètres › Équipe." },
  { pattern: "last_admin_required", message: "Il doit rester au moins un administrateur actif : nommez-en un autre avant de changer ce rôle ou de retirer cet accès." },
  { pattern: "profile_email_change_not_allowed", message: "L'adresse de connexion identifie votre compte : elle ne se modifie pas depuis le profil." },
  { pattern: "role_assignment_forbidden", message: "Seul un administrateur attribue ce rôle : choisissez Formateur ou Assistant." },
  { pattern: "hr_staff_email_already_member", message: "Cette adresse est déjà celle d'un membre de l'organisme : son accès se gère dans Paramètres › Équipe." },
  { pattern: "instructor_signature_method_removed", message: "La méthode de signature ne s'envoie plus : la signature du formateur se trace ou se tape sur la feuille." },
  // Suppressions refusées : des heures établies en dépendent.
  { pattern: "session_has_registrations", message: "Cette session a des inscriptions ou des feuilles d'émargement finalisées : annulez-la par « Changer le statut » plutôt que de la supprimer." },
  { pattern: "registration_has_counted_presence", message: "Cette inscription a des présences comptées sur des feuilles finalisées : déclarez un abandon plutôt que de la supprimer." },
  { pattern: "contact_has_counted_presence", message: "Ce contact a des présences comptées sur des feuilles d'émargement finalisées : ses heures figurent au certificat et au dossier de preuves, il ne se supprime pas." },
  { pattern: "attendance_session_status_locked", message: "Une session en brouillon, annulée ou clôturée ne reçoit pas de feuille d'émargement : ses heures ne seraient pas déclarées au BPF." },
  { pattern: "session_has_finalized_sheets", message: "Cette session a des feuilles d'émargement finalisées : ses heures figurent au certificat et au BPF, elle ne s'annule pas." },
  { pattern: "session_capacity_out_of_range", message: "La capacité d'une session est comprise entre 1 et 1000 participants : laissez le champ vide pour une session sans capacité." },
  { pattern: "session_dates_inverted", message: "La date de début ne peut pas suivre la date de fin : corrigez les dates de la session." },
  { pattern: "session_code_blank", message: "Le code de la session ne peut pas être vide." },
  { pattern: "session_participants_range_invalid", message: "Le minimum de participants est au moins 1 et ne dépasse pas le maximum." },
  { pattern: "session_field_too_long", message: "Un champ de la session dépasse la longueur autorisée (salle et plateforme : 100 caractères, notes : 10 000)." },
  { pattern: "session_location_too_long", message: "Le lieu de la session dépasse 500 caractères : raccourcissez-le." },
  { pattern: "session_remote_url_invalid", message: "Le lien de visioconférence doit être une adresse web (http:// ou https://, ou sans préfixe, complétée en https://) de 2000 caractères au plus, sans espace. Les autres schémas (javascript:, ftp:…) sont refusés." },
  { pattern: "session_slots_limit", message: "Une session compte au plus 1000 créneaux." },
  { pattern: "check_constraint_violated", message: "Une valeur saisie ne respecte pas une règle de cohérence des données : vérifiez les champs du formulaire." },
  { pattern: "session_has_unfinalized_sheets", message: "Cette session a des feuilles d'émargement non finalisées : finalisez-les avant de la clôturer, une session clôturée n'en finalise plus." },
  { pattern: "attendance_sheet_outside_session_dates", message: "La feuille est datée hors des dates de la session : modifiez les dates ou la fin réelle dans la fiche session (« Modifier ») avant de la finaliser." },
  { pattern: "session_actual_end_has_recorded_sheets", message: "Des feuilles d'émargement postérieures à cette fin réelle portent déjà des signatures ou des présences : elles ne se retirent pas. Choisissez une fin réelle au plus tôt le jour de la dernière." },
  { pattern: "program_has_sessions", message: "Ce programme a des sessions avec des inscriptions ou des heures émargées : il reste dans l'historique, avec leurs dossiers de preuves. S'il est publié, dépubliez-le pour le retirer du catalogue." },
  { pattern: "session_program_deleted", message: "Le programme de cette session a été supprimé : la session n'accepte plus d'inscription." },
  { pattern: "program_version_not_draft", message: "Seule une version en brouillon se supprime : une version en revue, active, obsolète ou archivée reste dans l'historique du programme." },
  { pattern: "program_deliverable_module_invalid", message: "Ce module n'appartient pas à ce programme : choisissez un module du programme, non supprimé." },
  { pattern: "program_deliverable_resource_invalid", message: "Cette ressource n'existe pas dans votre organisme : choisissez une ressource pédagogique de la liste." },
  { pattern: "program_module_parent_invalid", message: "Le module parent doit être un autre module de ce programme, non supprimé." },
  { pattern: "program_public_link_version_invalid", message: "Cette version n'appartient pas à ce programme : choisissez une version du programme, non supprimée." },
  { pattern: "slot_has_attendance_sheet", message: "Une feuille d'émargement est rattachée à ce créneau : annulez le créneau plutôt que de le supprimer, la feuille et son historique restent." },
  { pattern: "session_actual_end_before_start", message: "La fin réelle de la session ne peut pas précéder sa date de début : corrigez l'une des deux dates." },
  { pattern: "attendance_sheet_schedule_locked", message: "La feuille d'émargement est finalisée : ses horaires prévus ne se modifient plus." },
  { pattern: "attendance_sheet_overlap", message: "Une feuille couvre déjà cette date : une feuille « Journée » et une feuille de demi-journée ne peuvent pas coexister. Ouvrez la feuille existante." },
  { pattern: "attendance_duration_unknown", message: "Durée de la feuille inconnue : la session n'a aucun créneau à cette date et la feuille n'a pas d'horaires prévus. Saisissez les horaires prévus de la feuille, ou ajoutez un créneau à la session, avant de finaliser." },
  { pattern: "invalid_check_out_time", message: "Heure de départ invalide : elle doit suivre l'heure d'arrivée." },
  { pattern: "attendance_record_exists", message: "Ce stagiaire est déjà émargé sur cette feuille : les présences ont été rechargées. Vérifiez sa ligne puis enregistrez de nouveau." },
  { pattern: "attendance_registration_mismatch", message: "Cette inscription n'est pas active (annulée, abandonnée, en liste d'attente ou supprimée) ou n'appartient pas à cette session : l'émargement n'est pas possible. Rechargez la page." },
  { pattern: "attendance_slot_mismatch", message: "Le créneau choisi n'appartient pas à cette session ou n'est pas à la date de la feuille." },
  { pattern: "slot_instructor_not_in_session", message: "Ce formateur ne fait pas partie de l'équipe de la session. Ajoutez-le d'abord à l'équipe (Modifier la session > Formateurs), ou laissez le créneau aux formateurs de la session." },
  { pattern: "slot_date_finalized", message: "Une feuille d'émargement finalisée existe pour cette date : son planning est figé, car il détermine les heures établies." },
  // Pièce d'un dossier indisponible, servie ni par le dossier ni par son lien
  // direct (téléchargement d'un fichier ou d'un document, ajout de pièce).
  { pattern: "registration_document_unavailable", message: "Document impossible à générer : l'inscription ou sa session a été supprimée, ou la fiche du stagiaire a été supprimée ou ses données effacées. Aucun document ne se génère plus à son nom." },
  { pattern: "proof_folder_not_found", message: "Dossier de preuves indisponible : l'inscription, le stagiaire ou la session a été supprimé. Ses pièces ne se téléchargent plus, aucune pièce ne s'y ajoute et aucun export n'est possible." },
  { pattern: "bundle_obsolete", message: "Une pièce a été annulée ou supprimée depuis cet export, l'inscription ou la session a été supprimée, ou les données du stagiaire ou d'un formateur de la session ont été effacées : il n'est plus téléchargeable. Relancez l'export du dossier." },
  { pattern: "attendance_unsigned_presences", message: "Des présences ne sont ni signées ni cochées en émargement papier : faites-les signer, ou joignez le scan de la feuille signée avant de finaliser." },
  { pattern: "attendance_sign_wrong_day", message: "La feuille d'émargement se signe le jour de la séance." },
  { pattern: "attendance_already_signed", message: "Cette présence porte déjà une signature électronique : elle ne peut plus passer en émargement papier, ni revenir à l'électronique." },
  { pattern: "signature_only_by_trainee", message: "La signature électronique se recueille auprès du stagiaire (portail ou QR code). Depuis la feuille, seule la case « Papier » se coche." },
  { pattern: "signature_empty", message: "La signature est vide : tracez votre signature ou tapez votre nom avant de valider." },
  { pattern: "signature_form_invalid", message: "Signature non reproductible : seule une signature tracée, un nom tapé ou un fichier de l'organisme est accepté." },
  { pattern: "rules_acknowledgment_required", message: "Cochez l'attestation de prise de connaissance du règlement intérieur pour signer." },
  { pattern: "invalid_signature_method", message: "Méthode d'émargement inconnue : seule la case « Papier » peut être cochée sur une présence." },
  { pattern: "dropout_registration_mismatch", message: "Cette inscription n'est plus disponible pour un abandon (supprimée, ou type d'abandon retiré de la liste) : rechargez la page puis choisissez-la de nouveau." },
  { pattern: "dropout_final_status_use_close", message: "Ce statut clôt l'abandon : utilisez le bouton « Clôturer », qui enregistre la date et l'auteur de la clôture." },
  { pattern: "dropout_reference_mismatch", message: "Le type, le motif, la méthode de détection ou le référent choisi n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste." },
  { pattern: "dropout_completion_derived", message: "Le taux de réalisation d'un abandon se calcule à partir des feuilles d'émargement finalisées : il ne se saisit pas." },
  { pattern: "dropout_status_unknown", message: "Statut d'abandon inconnu : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "multipart_invalid", message: "Le fichier n'a pas pu être lu pendant l'envoi (connexion interrompue ou formulaire incomplet). Réessayez." },
  { pattern: "invalid_scheduled_times", message: "Horaires prévus invalides : l'heure de fin doit suivre l'heure de début." },
  { pattern: "attendance_schedule_required", message: "Aucun créneau de la session ne couvre cette date et cette période : saisissez les horaires prévus de la feuille (début et fin), ou planifiez d'abord le créneau dans l'onglet Planning de la session." },
  // Pièces de preuve seulement : les autres routes (illustration, photo)
  // renvoient FILE_TOO_LARGE avec un détail chiffré, rendu tel quel.
  { pattern: "proof_file_too_large", message: "Fichier trop volumineux pour une pièce de preuve. Réduisez sa taille (compression, résolution) puis réessayez." },
  { pattern: "unsupported_file_type", message: "Format non pris en charge : envoyez un PDF, un JPEG ou un PNG. Le type est vérifié sur le contenu du fichier." },
  { pattern: "file_empty", message: "Le fichier envoyé est vide. Choisissez un autre fichier." },
  { pattern: "import_entity_not_found", message: "L'élément auquel rattacher le fichier est introuvable : il a peut-être été supprimé. Rechargez la page." },
  { pattern: "import_entity_type_unknown", message: "Ce type d'élément n'accepte pas de fichier importé." },
  { pattern: "file_missing", message: "Aucun fichier n'a été envoyé. Choisissez un fichier puis réessayez." },
  { pattern: "network error", message: "La connexion au serveur a été interrompue avant la réponse. Vérifiez votre connexion puis réessayez ; l'opération a pu aboutir, consultez la liste avant de la renvoyer." },
  { pattern: "invalid_proof_category", message: "Type de pièce inconnu : choisissez-en un dans la liste." },
  { pattern: "proof_file_reserved", message: "Cette pièce s'ajoute depuis le dossier de preuves de l'inscription, ou depuis la feuille d'émargement pour un scan papier." },
  // Dépôt présigné (API et MCP) : le front envoie en multipart, mais un
  // client qui confirme un dépôt reçoit ces codes.
  { pattern: "upload_not_found", message: "Ce téléversement est introuvable : son lien a expiré ou il visait une autre pièce. Déposez le fichier de nouveau." },
  { pattern: "upload_already_confirmed", message: "Ce téléversement a déjà été enregistré : la pièce figure déjà au dossier." },
  { pattern: "upload_quota_exceeded", message: "Trop d'envois en attente de confirmation (5 par session, pièce ou dossier, 50 par organisme). Confirmez les envois en cours, ou attendez l'expiration de leurs liens (10 minutes au plus), avant d'en ajouter." },
  { pattern: "upload_size_mismatch", message: "Le fichier déposé n'a pas la taille annoncée : il a été retiré. Déposez-le de nouveau." },
  { pattern: "file_category_unknown", message: "Catégorie de fichier inconnue : choisissez l'une des catégories proposées." },
  { pattern: "too_many_attendance_sheets", message: "La session compte trop de feuilles d'émargement pour un dossier de preuves : découpez-la en plusieurs sessions." },
  { pattern: "attendance_statement_too_large", message: "Les signatures dépassent la taille maximale d'un relevé d'émargement : le relevé n'est pas établi, plutôt que d'omettre des signatures. Joignez à une partie des feuilles le scan de la feuille papier signée, puis relancez l'export." },
  { pattern: "rate_limited_bundle", message: "Un export de dossier est déjà en cours pour votre organisme, ou trois exports ont été demandés dans l'heure. Réessayez une fois l'export en cours terminé." },
  { pattern: "document_cancelled", message: "Ce document a été annulé : il n'est plus téléchargeable. Générez-en un nouveau si besoin." },
  { pattern: "enrollment_without_registration", message: "Ce parcours n'est rattaché à aucune inscription : ouvrez le dossier de preuves depuis la fiche de l'inscription." },
  { pattern: "tenant_legal_identity_required", message: "L'identité légale de l'organisme est incomplète (nom de l'organisme, SIRET, numéro de déclaration d'activité, ville, représentant légal et sa fonction) : complétez Paramètres > Organisation avant de générer ce document." },
  // Paramètres › Organisation : refus de l'enregistrement (422 de settings.rs).
  { pattern: "invalid_iban_checksum", message: "IBAN invalide : la clé de contrôle ne correspond pas. Vérifiez sa saisie." },
  { pattern: "invalid_iban_format", message: "IBAN mal formé : seuls les lettres et les chiffres de la norme ISO 13616 sont admis." },
  { pattern: "invalid_iban_length", message: "Longueur d'IBAN incorrecte pour le pays saisi." },
  { pattern: "unknown_iban_country", message: "Pays de l'IBAN non pris en charge : seuls les pays de la zone SEPA sont acceptés." },
  { pattern: "invalid_bic_format", message: "BIC mal formé : 8 ou 11 caractères alphanumériques (ISO 9362)." },
  { pattern: "company_name_provisional", message: "Ce nom est un nom provisoire : remplacez-le par le nom réel de l'organisme." },
  { pattern: "company_name_required", message: "Le nom de l'organisme n'a pas été retrouvé dans le répertoire SIRENE : saisissez-le pour continuer." },
  { pattern: "invalid_logo_url_format", message: "Adresse du logo invalide : une URL http(s):// sans espace, de 2 048 caractères au plus." },
  { pattern: "logo_format_unsupported", message: "Ce format d'image (WebP, GIF…) ne s'imprime pas sur les documents PDF : utilisez une image PNG, JPEG ou SVG." },
  { pattern: "vat_exemption_basis_required", message: "Ce document est à 0 % de TVA : indiquez le fondement de l'absence de TVA dans Paramètres > Organisation avant de le générer ou de l'émettre." },
  { pattern: "vat_chargeability_invalid", message: "Exigibilité de la TVA inconnue : choisissez-la dans la liste." },
  { pattern: "client_siren_required", message: "Le client est un professionnel : renseignez son numéro SIREN (ou son SIRET) sur la fiche du compte avant d'émettre la pièce." },
  { pattern: "vat_exemption_basis_invalid", message: "Fondement d'exonération de TVA inconnu : choisissez-en un dans la liste." },
  { pattern: "invalid_privacy_notice_url", message: "Adresse de la notice de confidentialité invalide : une URL https:// sans espace, de 2 048 caractères au plus." },
  { pattern: "legal_representative_too_long", message: "Le nom et la fonction du représentant légal sont limités à 150 caractères chacun." },
  { pattern: "organization_name_required", message: "Le nom de l'organisme est obligatoire : il figure sur le certificat de réalisation et les attestations." },
  { pattern: "organization_name_too_long", message: "Le nom de l'organisme est limité à 255 caractères." },
  // Documents générés : recherche, archivage, attestation de fin déjà délivrée.
  { pattern: "search_too_long", message: "La recherche est limitée à 100 caractères : raccourcissez-la." },
  { pattern: "task_role_unknown", message: "Ce rôle destinataire n'existe pas : choisissez un rôle dans la liste." },
  { pattern: "page_out_of_range", message: "Cette page n'existe pas : la pagination s'arrête à la page 10 000. Affinez la recherche ou les filtres." },
  { pattern: "document_not_archivable", message: "Seul un document généré ou signé, avec son PDF, peut être archivé. Rechargez la liste pour voir son statut actuel." },
  { pattern: "attestation_already_issued", message: "Cette inscription a déjà son attestation de fin de formation. Pour en délivrer une nouvelle, annulez la précédente dans ses documents." },
  { pattern: "attestation_registration_inactive", message: "Dans Qualneo, l'attestation de fin de formation est délivrée aux inscriptions dont la formation est menée à son terme (présent jusqu'au bout, terminée ou certifiée). Pour un stagiaire en abandon, générez le certificat de réalisation (heures effectivement suivies) ou l'attestation d'assiduité." },
  { pattern: "attendance_certificate_registration_inactive", message: "L'attestation d'assiduité ne se génère pas pour une inscription annulée, en liste d'attente ou en attente (devis, financement, signature)." },
  { pattern: "document_already_cancelled", message: "Ce document est déjà annulé : sa première annulation (auteur, date, motif) est conservée." },
  { pattern: "document_not_cancellable", message: "Un document signé ou archivé ne s'annule pas. Rechargez la liste pour voir son statut actuel." },
  { pattern: "scheduled_job_type_unsupported", message: "Ce type de tâche planifiée n'a pas de traitement : il ne peut pas être programmé." },
  { pattern: "scheduled_job_timeout_out_of_range", message: "Le délai d'une tâche planifiée va de 1 à 1 200 secondes (20 minutes, la durée maximale d'une exécution)." },
  { pattern: "scheduled_run_items_failed", message: "Des éléments de cette exécution ne sont pas partis : l'historique de la tâche les nomme, avec leur motif." },
  { pattern: "invalid_timezone", message: "Fuseau horaire inconnu : saisissez un nom de fuseau standard, par exemple Europe/Paris ou Indian/Reunion." },
  { pattern: "docuseal_config_ambiguous", message: "Plusieurs configurations DocuSeal actives et différentes existent pour votre organisme : aucune n'est choisie au hasard. Contactez le support pour n'en garder qu'une." },
  { pattern: "docuseal_webhook_unauthorized", message: "Événement de signature DocuSeal refusé : sa signature ne correspond pas à l'instance de votre organisme." },
  // Documents générés : refus d'annulation ou d'archivage (400, détail en anglais).
  { pattern: "document has no file", message: "Ce document n'a pas de PDF enregistré : il ne peut pas être archivé. Générez-le de nouveau." },
  { pattern: "document_generation_timeout", message: "La génération de ce document a dépassé le délai autorisé : relancez-la pour cet élément." },
  { pattern: "document_render_timeout", message: "La production de ce document a dépassé le délai autorisé : relancez-la." },
  { pattern: "template_entity_mismatch", message: "Ce modèle ne se génère pas pour ce type d'élément. Relancez la génération depuis la carte du document." },
  { pattern: "document_template_variable_missing", message: TEMPLATE_VARIABLE_MISSING_MESSAGE },
  { pattern: "invalid_civility", message: "Civilité inconnue : choisissez-en une dans la liste, ou « Non renseignée »." },
  // BPF, exports et créneaux.
  { pattern: "bpf_year_not_closed", message: "Cet exercice n'est pas clos : un BPF se déclare à partir du 1er janvier de l'année suivante." },
  { pattern: "bpf_year_invalid", message: "Exercice invalide : choisissez une année civile entre 2000 et 2100." },
  { pattern: "slot_instructor_unknown", message: "Ce formateur n'est pas une fiche formateur de l'organisme : choisissez-le dans RH › Formateurs." },
  { pattern: "export_file_unavailable", message: "Le fichier de cet export n'est plus disponible : relancez l'export." },
  // PDF signé supprimé ou purgé.
  { pattern: "signed_pdf_deleted", message: "Le PDF signé a été supprimé : il n'est plus consultable." },
  {
    pattern: "export_type_forbidden",
    message: "Cet export exige le droit Qualiopi › Lire : demandez-le à un administrateur.",
  },
  { pattern: "session not found", message: "Session introuvable. Elle a peut-être été supprimée." },
  // Dossiers d'abandon (indicateurs 12 et 31) : un dossier ouvert par inscription, clôture figée.
  { pattern: "dropout_already_open", message: "Un dossier d'abandon est déjà ouvert pour cette inscription : complétez-le ou clôturez-le avant d'en déclarer un autre." },
  { pattern: "dropout_closed", message: "Ce dossier d'abandon est clôturé : il ne se modifie plus. En cas de nouvel abandon après réintégration, déclarez un nouveau dossier." },
  { pattern: "dropout_registration_status_incompatible", message: "L'inscription n'est pas dans un état compatible avec ce statut de clôture (par exemple, une formation déjà terminée ne passe pas en abandon). Choisissez un autre statut final ou vérifiez l'inscription." },
  { pattern: "dropout_document_limit_reached", message: "Ce dossier d'abandon a atteint le nombre maximal de pièces (50). Retirez une pièce devenue inutile avant d'en ajouter une autre." },
  { pattern: "qualiopi_evidence_file_reserved", message: "Ce document ne peut pas servir de preuve d'indicateur : choisissez un fichier de votre organisme, hors pièces réservées (dossier de preuves, justificatifs d'absence, pièces d'abandon, archives)." },
  { pattern: "invalid_status_filter", message: "Filtre de statut inconnu : choisissez un statut dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "invalid_pipeline_stage", message: "Étape du pipeline inconnue : effacez le filtre puis choisissez une étape dans la barre du pipeline." },
  { pattern: "document_sequence_exhausted", message: "La série de numérotation de ce type de pièce est épuisée pour la période en cours. La pièce n'a pas été créée : contactez le support." },
  // Indicateurs Qualiopi : statut hors du référentiel, ou statut
  // manuel posé sur un indicateur qui se calcule.
  { pattern: "indicator_status_unknown", message: "Statut d'indicateur inconnu : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "qualiopi_referential_unknown", message: "Référentiel inconnu : choisissez-en un dans la liste des référentiels. Rechargez la page si le lien est ancien." },
  { pattern: "qualiopi_indicator_not_in_force", message: "Cet indicateur n'existe pas dans le référentiel en vigueur : choisissez-en un dans la liste." },
  { pattern: "indicator_status_computed", message: "Cet indicateur se calcule depuis vos données : vous pouvez seulement le déclarer non applicable, ou revenir au calcul." },
  // General
  { pattern: "cross-tenant access denied", message: "Accès refusé. Vous n'avez pas les droits pour cette ressource." },
  { pattern: "cannot modify system data", message: "Ces données système ne peuvent pas être modifiées." },
  { pattern: "template not found", message: "Le modèle de document n'est pas encore configuré. Contactez l'administrateur." },
  { pattern: "not found", message: "L'élément demandé est introuvable. Il a peut-être été supprimé." },
  { pattern: "validation error", message: "Erreur de validation. Vérifiez les champs du formulaire." },
  { pattern: "duplicate", message: "Un élément avec ces informations existe déjà." },
  { pattern: "conflict", message: "Un conflit a été détecté. Rechargez la page et réessayez." },
  { pattern: "too many requests", message: "Trop de requêtes. Veuillez patienter quelques instants." },
  { pattern: "service unavailable", message: "Le service est temporairement indisponible. Réessayez dans quelques instants." },
  { pattern: "internal server error", message: "Une erreur interne est survenue. Contactez le support si le problème persiste." },
  { pattern: "forbidden", message: "Accès refusé. Vous n'avez pas les droits nécessaires." },
  // Finalisation sans signature du formateur qu'un relevé reproduit (absente,
  // ou tracée librement avant que le serveur n'exige une image PNG ou
  // `typed:<nom>`) : l'écran propose « Re-signer ».
  { pattern: "instructor_signature_required", message: "La feuille ne peut pas être finalisée sans une signature valide du formateur : cliquez sur « Re-signer (formateur) », tracez la signature ou tapez son nom, puis finalisez." },
  // Heures, émargement et inscriptions.
  { pattern: "certificate_registration_inactive", message: "Le certificat de réalisation ne se génère pas pour une inscription annulée, en liste d'attente ou en attente (devis, financement, signature)." },
  { pattern: "attendance_status_not_allowed", message: "L'arrêt maladie ne se saisit plus, car c'est une donnée de santé : saisissez « Absent (justifié) » et déposez le justificatif dans l'onglet Justificatifs. Son contenu n'est lisible qu'avec le droit Administration › Lire." },
  { pattern: "registration_status_via_transition", message: "Le statut d'une inscription change par « Changer le statut » de sa fiche : la transition contrôle le passage et ouvre le dossier d'abandon si besoin." },
  { pattern: "registration_dropout_reason_required", message: "Indiquez le motif de l'abandon : il ouvre le dossier d'abandon du stagiaire." },
  { pattern: "attendance_rate_too_low", message: ATTENDANCE_RATE_TOO_LOW_MESSAGE },
  { pattern: "instructor_not_schedulable", message: "Ce formateur n'a pas de fiche planifiable : son classement interne ou externe ne peut pas être enregistré. Affectez-le d'abord à une session." },
  { pattern: "invalid_late_duration", message: "Le retard se saisit en minutes, entre 0 et 720." },
  { pattern: "certificate_url_read_only", message: "Le lien du certificat ne se saisit pas : générez le certificat depuis la fiche de l'inscription, il se télécharge ensuite depuis ses documents." },
  // Gardes de transition d'une session (details portés par le 422).
  { pattern: "session_end_date_not_reached", message: "La session ne peut pas être terminée avant sa date de fin." },
  { pattern: "session_attendance_rate_too_low", message: "La session ne peut pas être terminée : moins de 80 % de présence est établie par les feuilles d'émargement finalisées. Finalisez l'émargement des inscrits actifs." },
  { pattern: "session_certificates_missing", message: "La session ne peut pas être clôturée : des inscrits présents n'ont pas encore de certificat de réalisation. Générez-le depuis leur fiche d'inscription." },
  { pattern: "session_satisfaction_survey_missing", message: "La session ne peut pas être clôturée : aucune enquête de satisfaction à chaud n'a été envoyée à un stagiaire qui la doit encore (Qualiopi › Enquêtes), et aucune évaluation de satisfaction à chaud n'a été complétée pour l'un d'eux (un brouillon ne compte pas)." },
  { pattern: "session_override_forbidden", message: "Seul un administrateur peut forcer une transition de session." },
  { pattern: "session_transition_not_allowed", message: "Ce changement de statut n'est pas possible depuis le statut actuel de la session." },
  { pattern: "session_program_required", message: "Rattachez un programme à la session avant de la planifier." },
  { pattern: "session_venue_required", message: "Indiquez un lieu, ou cochez « distanciel », avant de planifier la session." },
  { pattern: "session_capacity_required", message: "Indiquez une capacité d'accueil supérieure à 0 avant de planifier la session." },
  { pattern: "session_instructor_required", message: "Affectez au moins un formateur avant de planifier la session." },
  { pattern: "session_opening_too_late", message: "Les inscriptions s'ouvrent au plus tard 48 h avant le début de la session." },
  { pattern: "session_threshold_not_met", message: "Le nombre minimum d'inscrits confirmés n'est pas atteint pour confirmer la session." },
  { pattern: "session_start_date_not_reached", message: "La session ne peut pas démarrer avant sa date de début." },
  { pattern: "session_cancellation_reason_required", message: "Indiquez le motif d'annulation de la session." },
  { pattern: "session_replacement_required", message: "Choisissez la session de remplacement avant de reporter celle-ci." },
  { pattern: "session_postponement_reason_required", message: "Indiquez le motif du report de la session." },
  // Communication d'une réclamation : canal absent du référentiel.
  { pattern: "complaint_channel_unknown", message: "Choisissez le canal de la communication dans la liste (email, téléphone, courrier…)." },
  { pattern: "complaint_channel_required", message: "Indiquez le canal par lequel la réclamation est arrivée (email, téléphone, courrier…)." },
  // Réclamation : référence d'un autre organisme, statut ou priorité inconnus (422).
  { pattern: "complaint_reference_mismatch", message: "La session, le programme, l'inscription, le contact, la personne assignée ou l'entrée de liste choisie n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste." },
  { pattern: "complaint_status_unknown", message: "Statut de réclamation inconnu : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "complaint_priority_unknown", message: "Priorité inconnue : choisissez-en une dans la liste (basse, moyenne, haute, urgente)." },
  // Action d'amélioration : statut absent du référentiel (422).
  { pattern: "improvement_action_status_unknown", message: "Ce statut d'action d'amélioration n'existe pas : choisissez-en un dans la liste." },
  // Action d'amélioration citée ailleurs (422).
  { pattern: "improvement_action_referenced", message: "Cette action d'amélioration est citée par des éléments de la base de connaissances ou de la veille : elle reste au registre. Pour l'écarter, passez-la au statut « Annulée »." },
  // Abandons, inscriptions, réclamations, actions d'amélioration.
  { pattern: "dropout_filter_restricted", message: "Ce type d'abandon désigne un motif réservé : le filtre demande le droit Administration › Lire." },
  { pattern: "registration_attestation_fields_read_only", message: "La date et le numéro de l'attestation de fin ne se saisissent pas : délivrez l'attestation depuis la fiche de l'inscription, ils se posent avec le document." },
  { pattern: "registration_transition_not_allowed", message: "Cette inscription ne peut pas passer à ce statut depuis son statut actuel. Rechargez la fiche pour voir les actions possibles." },
  { pattern: "complaint_closed", message: "La réclamation est clôturée : elle ne se modifie plus. Si le sujet revient, enregistrez-le comme une nouvelle réclamation et rappelez la précédente." },
  { pattern: "complaint_status_transition_not_allowed", message: "La réclamation ne peut pas passer à ce statut depuis son statut actuel. Rechargez la fiche pour voir les actions possibles." },
  { pattern: "complaint_status_via_action", message: "Ce statut se pose par son bouton (accuser réception, résoudre, clôturer), qui en garde la date et l'historique." },
  { pattern: "complaint_document_limit_reached", message: "Cette réclamation a atteint le nombre maximal de pièces. Supprimez une pièce devenue inutile avant d'en ajouter une autre." },
  { pattern: "dropout_field_restricted", message: "Le motif, sa catégorie, les notes d'entretien et le compte rendu des relances ne se modifient qu'avec le droit Administration › Lire, celui qui permet de les consulter." },
  { pattern: "improvement_reference_mismatch", message: "Le type, le statut, la personne assignée ou l'origine choisie n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste." },
  // Rappels d'inscription : seul l'email part.
  { pattern: "enrollment_reminder_channel_unsupported", message: "Seul l'email est envoyé : programmez le rappel par email, un rappel par SMS ou notification ne partirait pas." },
  { pattern: "enrollment_reminder_recipient_unsupported", message: "Le rappel part par email : indiquez l'adresse email du destinataire, pas un téléphone ni un utilisateur." },
  { pattern: "enrollment_reminder_recipient_email_invalid", message: "L'adresse du destinataire du rappel n'est pas une adresse email valide." },
  // Note d'un juré externe (portail jury).
  { pattern: "jury_grade_comment_required", message: "Ce résultat demande un commentaire : précisez votre appréciation avant d'enregistrer la note." },
  { pattern: "jury_grade_status_unknown", message: "Ce résultat n'existe pas : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "jury_registration_not_in_session", message: "Ce candidat n'est pas inscrit à la session de ce jury : rechargez la page pour retrouver la liste des candidats." },
  { pattern: "jury_grade_text_too_long", message: "Votre commentaire ou votre note privée dépasse 5000 caractères : raccourcissez-le avant d'enregistrer la note." },
  { pattern: "jury_competency_block_unknown", message: "Ce bloc de compétences n'appartient pas au programme de ce jury : rechargez la page pour retrouver la liste des blocs." },
  { pattern: "enrollment_reminder_recipient_not_linked", message: "Le rappel part au stagiaire ou à une personne liée à son inscription : un contact de son entreprise ou le représentant signataire de sa convention." },
  { pattern: "competency_framework_in_use", message: "Ce référentiel est utilisé par des programmes : retirez ses compétences de ces programmes avant de le supprimer." },
  { pattern: "competency_block_has_grades", message: "Des notes du jury portent sur ce bloc de compétences : il ne se supprime pas, sinon ces notes deviendraient des notes globales." },
  // Suppressions commerciales : les pièces envoyées ou payées restent au dossier.
  { pattern: "quote_not_deletable", message: "Seul un devis en brouillon, jamais envoyé, se supprime : un devis envoyé ou accepté reste au dossier." },
  { pattern: "invoice_has_payments", message: "Des paiements sont enregistrés sur cette facture : elle ne se supprime pas, pour que ces paiements gardent leur facture." },
  { pattern: "account_has_commercial_records", message: "Ce compte porte des devis, factures ou dossiers OPCO : ces pièces doivent rester consultables, le compte ne se supprime pas." },
  // Programme : prérequis saisis en liste.
  { pattern: "program_prerequisites_text_unsupported", message: "Les prérequis se saisissent un par un, dans la liste des prérequis du programme (ou activez « Cette formation ne nécessite aucun prérequis »)." },
  // Contrat individuel : parties, délai de rétractation, paiements.
  { pattern: "contract_parties_locked", message: "Le contrat n'est plus en cours de signature : ses parties et leurs signatures ne se modifient plus." },
  { pattern: "withdrawal_period_out_of_range", message: "Le délai de rétractation est de 10 jours au moins (article L6353-5 du Code du travail). Qualneo accepte une valeur jusqu'à 90 jours." },
  { pattern: "deposit_percentage_out_of_range", message: "Le plafond d'acompte du contrat est compris entre 0 et 30 % du prix convenu : il ne peut pas dépasser 30 % (article L6353-6 du Code du travail)." },
  { pattern: "contract_conclusion_mode_invalid", message: "Mode de conclusion inconnu : choisissez « dans les locaux de l'organisme », « à distance » ou « hors établissement »." },
  { pattern: "contract_early_start_not_applicable", message: "Ce contrat a été signé dans les locaux de l'organisme : il n'a pas de délai de 14 jours, la demande de démarrage anticipé ne s'applique pas." },
  { pattern: "contract_early_start_channel_not_durable", message: "Pour un contrat conclu hors établissement, la demande de démarrage anticipé doit être recueillie sur papier ou sur support durable (article L. 221-25, premier alinéa, première phrase) : choisissez « E-mail du stagiaire » ou « Papier signé »." },
  { pattern: "contract_amendment_mention_invalid", message: "Mentions invalides : seul un avenant indique les mentions de l'article L6353-4 du Code du travail qu'il modifie, choisies dans la liste." },
  { pattern: "contract_amended_contract_invalid", message: "Contrat modifié invalide : seul un avenant désigne le contrat qu'il modifie, un contrat de l'organisme qui n'est pas lui-même un avenant." },
  { pattern: "convention_individual_mentions_missing", message: "Ce contrat avec un particulier ne porte pas toutes les mentions de l'article L6353-4 du Code du travail : complétez-les depuis l'onglet Convention avant de générer le document." },
  { pattern: "contract_not_signed", message: CONTRACT_NOT_SIGNED_MESSAGE },
  { pattern: "contract_withdrawn", message: "Paiement refusé : le stagiaire s'est rétracté." },
  { pattern: "contract_withdrawal_period_running", message: WITHDRAWAL_PERIOD_RUNNING_FALLBACK },
  { pattern: "contract_deposit_cap_exceeded", message: DEPOSIT_CAP_EXCEEDED_FALLBACK },
  { pattern: "contract_price_required", message: "Un contrat individuel porte son prix : saisissez le montant HT ; le plafond d'acompte de 30 % du prix convenu (article L6353-6 du Code du travail) se calcule, dans Qualneo, sur le prix TTC qui en découle." },
  { pattern: "contract_party_required", message: "Un contrat individuel désigne son client (compte) ou son stagiaire (contact) : c'est par eux que ses paiements s'y rattachent." },
  { pattern: "contract_not_individual", message: "Le droit de rétractation ne s'applique qu'aux contrats individuels, conclus par une personne qui finance elle-même sa formation (article L6353-5 du Code du travail)." },
  { pattern: "contract_already_withdrawn", message: "La rétractation est déjà enregistrée sur ce contrat." },
  { pattern: "contract_status_not_withdrawable", message: CLOSED_CONTRACT_WITHDRAWAL_MESSAGE },
  { pattern: "contract_withdrawal_letter_late", message: LETTER_LATE_FALLBACK },
  { pattern: "invoice_credit_note_not_deletable", message: CREDIT_NOTE_NOT_DELETABLE_MESSAGE },
  { pattern: "invoice_numbered_not_deletable", message: "Cette facture porte déjà un numéro : elle ne peut pas être supprimée, car la suite des numéros de facture doit rester continue (art. 242 nonies A de l'annexe II au CGI) ; annulez-la en émettant un avoir." },
  { pattern: "invoice_is_credit_note", message: "Cette pièce est un avoir : un avoir ne s'annule pas par un autre avoir. Tant qu'il est en brouillon, modifiez-le." },
  { pattern: "invoice_credit_note_amount_invalid", message: "Montant d'avoir refusé : il doit être supérieur à 0 €, sauf pour annuler une facture à 0 € par un seul avoir total à 0 €." },
  { pattern: "contract_quote_invalid", message: "Devis invalide ou introuvable : choisissez un devis accepté du même compte ou du même contact que le contrat." },
  // Suppressions refusées : contrat signé, analyse validée, devis envoyé.
  { pattern: "contract_not_deletable", message: "Seul un contrat en brouillon, sans aucune signature, peut être supprimé." },
  { pattern: "needs_assessment_validated_linked", message: "Cette analyse des besoins est validée et rattachée à une inscription : archivez-la au lieu de la supprimer." },
  { pattern: "quote_has_invoice", message: "Ce devis a déjà une facture : il ne peut plus être révoqué ni modifié." },
  { pattern: "quote_signature_pending", message: "Une signature électronique est en cours sur ce devis : annulez-la avant de le révoquer." },
  { pattern: "quote_not_invoiceable", message: "Seul un devis accepté se facture : faites d'abord accepter ce devis par le client." },
  { pattern: "quote_not_editable", message: "Ce devis n'est plus modifiable (il a été envoyé, accepté, refusé ou révoqué). Révoquez-le pour créer une nouvelle version." },
  { pattern: "enrollment_convention_exists", message: "Cette inscription a déjà sa convention : ouvrez-la depuis l'onglet Convention." },
  // Veille : statut choisi dans une liste périmée.
  { pattern: "watch_status_invalid", message: "Statut de veille inconnu : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  // Accessibilité : niveau de visibilité d'un besoin.
  { pattern: "visibility_level_unknown", message: "Ce niveau de visibilité n'existe pas : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  // Accessibilité : type ou besoin choisi dans une liste périmée.
  { pattern: "invalid disability type", message: "Ce type de handicap n'existe pas dans votre organisme : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "invalid accommodation type", message: "Ce type d'adaptation n'existe pas dans votre organisme : choisissez-en un dans la liste. Rechargez la page si la liste vient de changer." },
  { pattern: "invalid accessibility need", message: "Ce besoin n'existe plus pour ce contact : rechargez la page." },
  // Indicateurs marketing : définition qui porte des valeurs.
  { pattern: "kpi_definition_has_values", message: "Cet indicateur a des valeurs saisies sur des programmes : il ne peut pas être supprimé. Désactivez-le pour le retirer du catalogue en gardant son historique." },
  // Import d'un programme : seul son PDF déposé se lit.
  { pattern: "program_import_source_invalid", message: "Ce fichier n'a pas été déposé pour importer un programme : déposez le PDF du programme depuis l'import." },
  // Invitation : fenêtre des envois automatiques.
  { pattern: "survey_assignment_session_not_taking_place", message: "Cette session n'a pas lieu (brouillon, annulée ou reportée) : l'invitation ne partirait pas. Choisissez une session qui a lieu." },
  { pattern: "survey_assignment_schedule_out_of_window", message: "Cette date d'envoi est passée de plus de 7 jours, à plus d'un an, ou le lien du questionnaire serait déjà expiré : choisissez une autre date." },
  // Résiliation d'un organisme, export complet, purge.
  { pattern: "tenant_closed", message: "Ce service n'est plus disponible, car l'organisme de formation a fermé son espace. Pour toute question sur vos données, contactez-le directement." },
  { pattern: "tenant_termination_date_invalid", message: "La date d'effet de la résiliation doit être comprise entre demain et dans un an." },
  { pattern: "tenant_termination_demo_refused", message: "L'organisme de démonstration ne peut pas être résilié." },
  { pattern: "tenant_termination_already_scheduled", message: "Une résiliation est déjà programmée pour cet organisme. Annulez-la avant d'en programmer une autre." },
  { pattern: "tenant_termination_not_found", message: "Il n'y a plus de résiliation à annuler, car elle a déjà été annulée ou a déjà pris effet. Une résiliation effective ne peut plus être annulée." },
  // Effacement partiel des données personnelles (RGPD art. 17)
  { pattern: "erasure_identity_not_verified", message: "Cochez la case : vous devez avoir vérifié l'identité du demandeur avant d'effacer ses données." },
  { pattern: "contact_erasure_blocked", message: "Effacement impossible pour l'instant : un dossier en cours concerne ce contact." },
  { pattern: "contact_already_erased", message: "Les données personnelles de ce contact ont déjà été effacées." },
  { pattern: "instructor_erasure_blocked", message: "Effacement impossible pour l'instant : un dossier en cours concerne ce formateur." },
  { pattern: "instructor_already_erased", message: "Les données personnelles de ce formateur ont déjà été effacées." },
  { pattern: "jury_member_already_erased", message: "Les données personnelles de ce membre du jury ont déjà été effacées." },
  { pattern: "erasure_request_date_invalid", message: "La date de réception de la demande doit être comprise entre il y a un an et aujourd'hui." },
  { pattern: "jury_has_deliberation", message: "Ce jury a des notes ou une délibération, ou il délibère : il ne se supprime plus, ses notes et sa délibération restent la preuve de la délibération." },
  { pattern: "jury_member_has_grades", message: "Ce membre a déjà noté, ou le jury délibère : il ne se retire plus, ses notes restent la preuve de la délibération. Pour effacer ses données personnelles, utilisez « Effacer les données personnelles »." },
  { pattern: "tenant_purge_started", message: "L'effacement des données de l'organisme a commencé, l'export complet ne peut donc plus être demandé." },
  { pattern: "organization_archive_not_ready", message: "Cet export ne peut pas être téléchargé, car il est encore en préparation, a échoué, a expiré ou a été retiré après un effacement de données. Demandez un nouvel export." },
  // Conservation des données : durée réglée par l'organisme.
  { pattern: "retention_out_of_bounds", message: "Cette durée sort des bornes de la catégorie. Respectez son minimum et son maximum." },
  { pattern: "retention_category_not_found", message: "Cette catégorie de conservation n'existe pas. Rechargez la page." },
  {
    pattern: "session_evidence_purged",
    message:
      "Les preuves de cette session ont été effacées au terme de leur durée de conservation : aucune pièce de preuve ne peut plus y être produite ni déposée.",
  },
  // Tâche planifiée : exécution en attente de reprise ou en cours.
  { pattern: "scheduled_job_run_pending", message: "Une exécution de cette tâche est déjà en attente ou en cours. Attendez qu'elle se termine avant d'en lancer une autre. Une exécution interrompue est libérée au bout de 30 minutes." },
  // Lecture ou écriture d'un élément absent ou d'un autre organisme.
  { pattern: "not_found", message: "L'élément demandé est introuvable. Il a peut-être été supprimé." },
]

/**
 * Motifs de refus d'une transition d'inscription (`details.reason_code` du
 * 422 REGISTRATION_TRANSITION_NOT_ALLOWED), avec la marche à suivre.
 */
const REGISTRATION_TRANSITION_REASONS: Record<string, string> = {
  NOT_IN_WORKFLOW: "Cette inscription ne peut pas passer à ce statut depuis son statut actuel. Rechargez la fiche pour voir les actions possibles.",
  QUOTE_REQUIRED: "Créez le devis de l'inscription avant de l'envoyer.",
  QUOTE_NOT_ACCEPTED: "Le devis doit être accepté avant la demande de financement.",
  FUNDING_NOT_APPROVED: "Le financement doit être accordé avant l'envoi de la convention.",
  CONVENTION_NOT_SIGNED: "La convention doit être signée avant de confirmer l'inscription.",
  PREREQUISITES_NOT_VALIDATED: "Validez les prérequis du stagiaire avant cette étape (positionnement à l'entrée, indicateur 8).",
  SESSION_EVIDENCE_PURGED:
    "Les preuves de cette session ont été effacées au terme de leur durée de conservation : aucune pièce de preuve ne peut plus y être produite ni déposée, et son dossier ne s'exporte plus.",
  SESSION_FULL: "La session est complète : inscrivez le stagiaire en liste d'attente.",
  SESSION_HAS_AVAILABLE_SPOTS: "La session a encore des places : confirmez l'inscription plutôt que de la mettre en liste d'attente.",
  SESSION_NOT_OPEN: "La session n'est ni planifiée, ni ouverte, ni confirmée : elle n'accepte pas de confirmation d'inscription.",
  SESSION_NOT_IN_PROGRESS: "La session n'est pas en cours : démarrez-la avant de passer le stagiaire en formation.",
  SESSION_NOT_STARTED: "La session n'a pas encore commencé.",
  SESSION_NOT_ENDED: "La session n'est pas terminée : l'inscription se termine à partir du dernier jour de la session.",
  PROGRAM_NOT_CERTIFYING: "Le programme de cette session n'est pas certifiant.",
  CANCELLATION_REASON_REQUIRED: "Indiquez le motif de l'annulation.",
  CANCEL_AFTER_SESSION_START: "La session a commencé : le départ du stagiaire se déclare en abandon, pas en annulation.",
  CANCEL_WITH_COUNTED_PRESENCE: "Le stagiaire a des présences émargées : son départ se déclare en abandon, pas en annulation.",
  UNKNOWN_STATUS: "Ce statut d'inscription n'existe pas. Rechargez la fiche pour choisir parmi les actions proposées.",
  SESSION_ALREADY_STARTED: "La session a commencé : réintégrez le stagiaire « en formation », pas « inscrit ».",
}

/**
 * Returns true if a string looks like HTML (e.g. nginx 502 Bad Gateway page).
 */
function looksLikeHtml(s: string): boolean {
  return /^\s*</.test(s) || /<!doctype/i.test(s)
}

/** Écriture sans réponse : son effet est inconnu. */
export const WRITE_INTERRUPTED_MESSAGE =
  "La réponse du serveur n'est pas arrivée : l'action a peut-être été enregistrée. Vérifiez avant de recommencer."

const WRITE_METHODS = new Set(["post", "put", "patch", "delete"])

/**
 * Réponse coupée en route : le serveur a pu finir.
 * 502 et 504 viennent du mandataire.
 * 520 et 524 viennent du frontal Cloudflare.
 * Il coupe l'origine muette, ou après 100 s.
 */
const CUT_RESPONSE_STATUSES: ReadonlySet<number> = new Set([502, 504, 520, 524])

/** Page HTML d'indisponibilité : statuts coupés et 503. */
const UNAVAILABLE_PAGE_STATUSES: ReadonlySet<number> = new Set([...CUT_RESPONSE_STATUSES, 503])

/** Méthode HTTP d'une requête axios en échec, en minuscules. */
function requestMethod(error: unknown): O.Option<string> {
  return pipe(
    O.fromPredicate(isAxiosError)(error),
    O.chain((failure) => O.fromNullable(failure.config?.method)),
    O.map((method) => method.toLowerCase()),
  )
}

/**
 * Écriture coupée (502, 504, 520, 524 ou sans statut) : le serveur a pu l'exécuter.
 * @example isInterruptedWrite({ config: { method: "post" } }) // true (erreur réseau)
 */
function isInterruptedWrite(error: unknown): boolean {
  const writes = pipe(
    requestMethod(error),
    O.exists((method) => WRITE_METHODS.has(method)),
  )
  const status = extractErrorStatus(error)
  return writes && (status === null || CUT_RESPONSE_STATUSES.has(status))
}

/**
 * Extract a readable error detail string from various API error shapes.
 */
function extractErrorDetail(error: unknown): string {
  if (isInterruptedWrite(error)) return WRITE_INTERRUPTED_MESSAGE
  const response = (error as { response?: { status?: number; data?: unknown } }).response
  const data = response?.data
  const status = response?.status

  // Non-JSON response (e.g. nginx HTML error page)
  if (typeof data === "string" && looksLikeHtml(data)) {
    if (status !== undefined && UNAVAILABLE_PAGE_STATUSES.has(status)) {
      return "Le serveur est temporairement indisponible, veuillez réessayer."
    }
    return "Une erreur inattendue est survenue."
  }

  if (typeof data === "string") {
    return data
  }

  const problem = data as ApiError | undefined

  // RFC 7807 validation errors extension
  if (problem?.errors) {
    const errors = problem.errors as Record<string, Array<{ message: string }>>
    const firstField = Object.keys(errors)[0]
    if (firstField && errors[firstField]?.[0]?.message) {
      return errors[firstField][0].message
    }
  }

  // Standard RFC 7807 fields
  if (problem?.detail) return problem.detail
  if (problem?.title) return problem.title

  // Nested error object shape: { error: { message: "..." } }
  const nested = data as { error?: { message?: string } } | undefined
  if (nested?.error?.message) return nested.error.message

  // Axios-level message
  const axiosError = error as { message?: string } | undefined
  if (axiosError?.message) return axiosError.message

  return String(error)
}

/**
 * Extract the structured `code` field from a Problem Details extension
 * (RFC 7807 §3.2). Backend errors like INVITATION_PENDING expose machine-readable
 * codes that take precedence over substring matching on the detail string.
 */
export function extractErrorCode(error: unknown): string | null {
  // Requête réussie : `error` est nul ou absent.
  if (typeof error !== "object" || error === null) return null
  const data = (error as { response?: { data?: unknown } }).response?.data
  const code = (data as { code?: unknown } | undefined)?.code
  if (typeof code === "string" && code.length > 0) {
    return code
  }
  return null
}

/** HTTP status of an API error, `null` for a network error or a non-HTTP failure. */
export function extractErrorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null
  const status = (error as { response?: { status?: unknown } }).response?.status
  return typeof status === "number" ? status : null
}

function recordField(key: string): (value: unknown) => O.Option<unknown> {
  return (value) =>
    pipe(
      O.fromNullable(value),
      O.filter((v): v is Record<string, unknown> => typeof v === "object"),
      O.chain((record) => (key in record ? O.fromNullable(record[key]) : O.none)),
    )
}

/**
 * Champ `key` à la racine du Problem JSON d'une erreur.
 *
 * @example
 * extractErrorBodyField({ response: { data: { upgrade_plan: "equipe" } } }, "upgrade_plan") // O.some("equipe")
 */
export function extractErrorBodyField(error: unknown, key: string): O.Option<unknown> {
  return pipe(O.some(error), O.chain(recordField("response")), O.chain(recordField("data")), O.chain(recordField(key)))
}

/** Champ `details.<key>` du Problem JSON. */
export function extractErrorDetailField(error: unknown, key: string): O.Option<unknown> {
  return pipe(extractErrorBodyField(error, "details"), O.chain(recordField(key)))
}

// Date de séance au format français.
// `AAAA-MM-JJ` est une date calendaire, sans fuseau.
const SHEET_DAY: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" }

/** Signature hors du jour de la séance, datée quand le serveur donne `details.sheet_date`. */
function signWrongDayMessage(error: unknown): string {
  const day = pipe(
    extractErrorDetailField(error, "sheet_date"),
    O.filter((date): date is string => typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)),
    O.match(
      () => "",
      (date) => `, le ${formatIsoDateFr(date, SHEET_DAY)}`,
    ),
  )
  return `La feuille d'émargement se signe le jour de la séance${day}.`
}

/** Jour au format `AAAA-MM-JJ`. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Date `AAAA-MM-JJ` de `details.<key>`, au format français. */
function detailDate(error: unknown, key: string): O.Option<string> {
  return pipe(
    extractErrorDetailField(error, key),
    O.filter((date): date is string => typeof date === "string" && ISO_DAY.test(date)),
    O.map((date) => formatIsoDateFr(date, SHEET_DAY)),
  )
}

/** Borne entière en mois lue dans `details`. */
function detailMonths(error: unknown, key: string): O.Option<number> {
  return pipe(
    extractErrorDetailField(error, key),
    O.filter((months): months is number => typeof months === "number" && Number.isInteger(months)),
  )
}

/** Durée hors bornes : le minimum et le maximum, en mois. */
function retentionBoundsMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("min", detailMonths(error, "min_months")),
    O.apS("max", detailMonths(error, "max_months")),
    O.match(
      () => "Cette durée sort des bornes de la catégorie. Respectez son minimum et son maximum.",
      ({ min, max }) => `La durée de cette catégorie va de ${min} à ${max} mois.`,
    ),
  )
}

/** Date d'effet de résiliation refusée : ses deux bornes. */
function terminationDateMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("min", detailDate(error, "min_date")),
    O.apS("max", detailDate(error, "max_date")),
    O.match(
      () => "La date d'effet de la résiliation doit être comprise entre demain et dans un an.",
      ({ min, max }) => `La date d'effet de la résiliation doit être comprise entre le ${min} et le ${max}.`,
    ),
  )
}

/** Fin réelle refusée : message avec ses deux bornes. */
function sessionActualEndRangeMessage(error: unknown): string {
  return pipe(
    detailDate(error, "today"),
    O.match(
      () => "La fin réelle de la session ne peut pas être postérieure à aujourd'hui, ni antérieure à la dernière feuille d'émargement finalisée de la session.",
      (today) =>
        pipe(
          detailDate(error, "last_finalized_sheet_date"),
          O.match(
            () => `La fin réelle de la session ne peut pas être postérieure à aujourd'hui (${today}).`,
            (last) => `La fin réelle doit être comprise entre le ${last} (dernière feuille d'émargement finalisée) et le ${today} (aujourd'hui).`,
          ),
        ),
    ),
  )
}

/** Montant du Problem JSON (chaîne décimale du serveur) au format français. */
function detailRateCapAmount(error: unknown, key: string, fractionDigits: number): O.Option<string> {
  return pipe(
    extractErrorDetailField(error, key),
    O.map((value) => Number(value)),
    O.filter((value) => Number.isFinite(value) && value > 0),
    O.map((value) =>
      new Intl.NumberFormat("fr-FR", { minimumFractionDigits: fractionDigits, maximumFractionDigits: 2 }).format(value),
    ),
  )
}

/** Taux formateur au-delà du plafond de l'organisme, chiffré quand le serveur le donne. */
function instructorRateAboveCapMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("cap", detailRateCapAmount(error, "daily_rate_cap_ht", 2)),
    O.apS("hours", detailRateCapAmount(error, "hours_per_day", 0)),
    O.match(
      () => "Ce taux formateur dépasse le plafond journalier fixé par votre organisme (Paramètres › Organisation).",
      ({ cap, hours }) =>
        `Ce taux formateur dépasse le plafond de ${cap} € HT par jour fixé par votre organisme, pour une journée de ${hours} h. Saisissez un taux plus bas.`,
    ),
  )
}

/** Dates de session qui excluraient des feuilles finalisées : leurs dates. */
function sessionDatesExcludeSheetsMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("first", detailDate(error, "first_finalized_sheet_date")),
    O.apS("last", detailDate(error, "last_finalized_sheet_date")),
    O.match(
      () => "Les dates de la session doivent couvrir ses feuilles d'émargement finalisées : le début au plus tard le jour de la première, la fin au plus tôt le jour de la dernière.",
      ({ first, last }) =>
        `Les dates de la session doivent couvrir ses feuilles d'émargement finalisées : début au plus tard le ${first}, fin au plus tôt le ${last}.`,
    ),
  )
}

/** Créneau dont la date sort de la session : ses dates. */
function slotDateOutOfSessionMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("start", detailDate(error, "start_date")),
    O.apS("end", detailDate(error, "end_date")),
    O.match(
      () => "La date du créneau doit tomber dans les dates de la session : modifiez la date du créneau, ou d'abord les dates de la session.",
      ({ start, end }) => `La date du créneau doit tomber dans les dates de la session, du ${start} au ${end}.`,
    ),
  )
}

/** Créneaux hors des nouvelles dates de la session. */
function sessionDatesExcludeSlotsMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("count", detailCount(error)),
    O.apS("first", detailDate(error, "first_slot_date")),
    O.apS("last", detailDate(error, "last_slot_date")),
    O.match(
      () => "Les dates de la session doivent couvrir ses créneaux : déplacez ou supprimez ceux qui tombent hors des nouvelles dates.",
      ({ count, first, last }) =>
        count === 1
          ? `Un créneau, le ${first}, tombe hors des nouvelles dates de la session : déplacez-le ou supprimez-le d'abord.`
          : `${count} créneaux, du ${first} au ${last}, tombent hors des nouvelles dates de la session : déplacez-les ou supprimez-les d'abord.`,
    ),
  )
}

/** Statut de session qui interdit l'émargement, en clair. */
const LOCKED_SESSION_STATUS_LABELS: Record<string, string> = {
  draft: "en brouillon",
  cancelled: "annulée",
  closed: "clôturée",
}

/** Feuille refusée sur une session verrouillée : son statut. */
function sessionStatusLockedMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "status"),
    O.filter((status): status is string => typeof status === "string"),
    O.chain((status) => O.fromNullable(LOCKED_SESSION_STATUS_LABELS[status])),
    O.match(
      () => pipe(translateErrorCode("ATTENDANCE_SESSION_STATUS_LOCKED"), O.getOrElse(() => "")),
      (label) => `Cette session est ${label} : elle ne reçoit pas de feuille d'émargement, ses heures ne seraient pas déclarées au BPF.`,
    ),
  )
}

/** Feuille hors des dates de la session : ses bornes. */
function sheetOutsideSessionDatesMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("start", detailDate(error, "start_date")),
    O.apS("end", detailDate(error, "end_date")),
    O.match(
      () => pipe(translateErrorCode("ATTENDANCE_SHEET_OUTSIDE_SESSION_DATES"), O.getOrElse(() => "")),
      ({ start, end }) =>
        `La feuille est datée hors de la session (du ${start} au ${end}) : modifiez les dates ou la fin réelle dans la fiche session (« Modifier ») avant de la finaliser.`,
    ),
  )
}

/** Fin réelle refusée : la première feuille déjà renseignée après elle. */
function actualEndRecordedSheetsMessage(error: unknown): string {
  return pipe(
    detailDate(error, "first_sheet_date"),
    O.match(
      () => pipe(translateErrorCode("SESSION_ACTUAL_END_HAS_RECORDED_SHEETS"), O.getOrElse(() => "")),
      (first) =>
        `La feuille d'émargement du ${first}, postérieure à cette fin réelle, porte déjà des signatures ou des présences : elle ne se retire pas. Choisissez une fin réelle au plus tôt ce jour-là.`,
    ),
  )
}

/** Clôture refusée : la première feuille encore à finaliser. */
function unfinalizedSheetsMessage(error: unknown): string {
  return pipe(
    detailDate(error, "first_sheet_date"),
    O.match(
      () => pipe(translateErrorCode("SESSION_HAS_UNFINALIZED_SHEETS"), O.getOrElse(() => "")),
      (first) =>
        `Cette session a des feuilles d'émargement non finalisées, la première datée du ${first} : finalisez-les avant de la clôturer, une session clôturée n'en finalise plus.`,
    ),
  )
}

/** Suppression de session refusée : le remède que permet son statut. */
function sessionHasRegistrationsMessage(error: unknown): string {
  const hasHoursDetail = pipe(
    extractErrorDetailField(error, "has_finalized_sheets"),
    O.filter((value): value is boolean => typeof value === "boolean"),
  )
  const statusDetail = pipe(
    extractErrorDetailField(error, "current_status"),
    O.filter((value): value is string => typeof value === "string"),
  )
  return pipe(
    O.Do,
    O.apS("hasHours", hasHoursDetail),
    O.apS("status", statusDetail),
    O.match(
      () => pipe(translateErrorCode("SESSION_HAS_REGISTRATIONS"), O.getOrElse(() => "")),
      ({ hasHours, status }) => {
        if (hasHours) return "Une session qui a des heures émargées ne se supprime ni ne s'annule : elle reste dans l'historique."
        if (["planned", "open", "confirmed"].includes(status)) {
          return "Cette session a des inscriptions : annulez-la par « Changer le statut » plutôt que de la supprimer."
        }
        return "Cette session a des inscriptions : supprimez-les d'abord, ou gardez la session dans l'historique."
      },
    ),
  )
}

/** Feuille hors de l'action : fin réelle ou jour de l'organisme. */
function attendanceSheetDateMessage(error: unknown, code: string): string {
  const fallback = pipe(
    translateErrorCode(code),
    O.getOrElse(() => "La date de la feuille n'est pas acceptée."),
  )
  return code === "ATTENDANCE_SHEET_AFTER_ACTUAL_END"
    ? pipe(
        detailDate(error, "actual_end_date"),
        O.match(
          () => fallback,
          (end) => `La feuille est datée après la fin réelle de la session (${end}) : reportez la fin réelle dans la fiche session (« Modifier »).`,
        ),
      )
    : pipe(
        detailDate(error, "today"),
        O.match(
          () => fallback,
          (today) => `Une feuille datée d'un jour à venir ne se finalise pas : finalisez-la le jour de la séance, ou après (nous sommes le ${today}).`,
        ),
      )
}

const RATE_PERCENT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 })

/**
 * 422 ATTENDANCE_RATE_TOO_LOW (passage d'une inscription à « terminée ») :
 * taux établi et seuil, en pourcentages, quand le serveur les donne
 * (`details.rate`, `details.threshold`).
 */
function registrationAttendanceRateMessage(error: unknown): string {
  const percent = (key: string) =>
    pipe(
      extractErrorDetailField(error, key),
      O.filter((value): value is number => typeof value === "number" && Number.isFinite(value)),
    )
  return pipe(
    O.Do,
    O.apS("rate", percent("rate")),
    O.apS("threshold", percent("threshold")),
    O.match(
      () => ATTENDANCE_RATE_TOO_LOW_MESSAGE,
      ({ rate, threshold }) =>
        `L'inscription ne peut pas être terminée : ${RATE_PERCENT.format(rate)} % de présence établie par l'émargement finalisé, ${RATE_PERCENT.format(threshold)} % requis. Finalisez l'émargement de la session.`,
    ),
  )
}

/** Refus de transition d'inscription, précisé par `details.reason_code`. */
function registrationTransitionMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "reason_code"),
    O.filter((reason): reason is string => typeof reason === "string"),
    O.chain((reason) => O.fromNullable(REGISTRATION_TRANSITION_REASONS[reason])),
    O.getOrElse(() => REGISTRATION_TRANSITION_REASONS.NOT_IN_WORKFLOW),
  )
}

/** Finalisation refusée : contrat sans partie, ou partie sans signature. */
function contractPartyNotSignedMessage(error: unknown): string {
  return pipe(
    extractErrorBodyField(error, "details"),
    O.filter((details): details is Record<string, unknown> => typeof details === "object"),
    O.filter((details) => "party_id" in details && O.isNone(O.fromNullable(details.party_id))),
    O.match(
      () => CONTRACT_PARTY_NOT_SIGNED_MESSAGE,
      () => "Ce contrat n'a aucune partie : ajoutez ses parties et enregistrez leur signature avant de le finaliser.",
    ),
  )
}

const CONTRACT_PARTY_NOT_SIGNED_MESSAGE =
  "Chaque partie doit avoir signé avant la finalisation : enregistrez la signature manquante dans l'onglet Signatures."

const EURO = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" })

/** Nombre de `details.<key>`, décimal sérialisé en chaîne compris. */
function detailNumber(error: unknown, key: string): O.Option<number> {
  return pipe(
    extractErrorDetailField(error, key),
    O.filter((value): value is number | string => typeof value === "number" || typeof value === "string"),
    O.map(Number),
    O.filter(Number.isFinite),
  )
}

/** Montant de `details.<key>`, en euros. */
function detailAmount(error: unknown, key: string): O.Option<string> {
  return pipe(
    detailNumber(error, key),
    O.map((value) => EURO.format(value)),
  )
}

/** Paiement pendant le délai de rétractation, daté par `details`. */
function withdrawalPeriodRunningMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("deadline", detailDate(error, "withdrawal_deadline")),
    O.apS("payable", detailDate(error, "first_payable_date")),
    O.match(
      () => WITHDRAWAL_PERIOD_RUNNING_FALLBACK,
      ({ deadline, payable }) =>
        `Paiement refusé : le délai de rétractation du stagiaire court jusqu'au ${deadline} inclus. ${PAYMENT_DURING_WITHDRAWAL_RULE} Vous pourrez enregistrer ce paiement à partir du ${payable}. Si une somme a déjà été reçue pendant ce délai, rapprochez-vous du stagiaire pour la lui restituer.`,
    ),
  )
}

/** Plafond du jour dépassé, chiffré par `details`. */
function depositCapExceededMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("price", detailAmount(error, "price")),
    O.apS("cap", detailAmount(error, "cap")),
    O.apS("remaining", detailAmount(error, "remaining")),
    O.match(
      () => DEPOSIT_CAP_EXCEEDED_FALLBACK,
      ({ price, cap, remaining }) =>
        `Paiement refusé : avec ce montant, le cumul des paiements dépasserait le plafond que Qualneo accepte à ce jour, soit ${cap} sur un prix convenu de ${price} TTC. À l'expiration du délai de rétractation, l'organisme ne peut exiger plus de 30 % du prix convenu (article L6353-6 du Code du travail) ; le solde est échelonné au fur et à mesure du déroulement de la formation, que Qualneo mesure d'après les heures dispensées. Montant encore acceptable : ${remaining}.`,
    ),
  )
}

/** Clé de `details` qui porte le dernier jour du délai. */
const WITHDRAWAL_DEADLINE_KEY = "withdrawal_deadline"

/** Finalisation pendant le délai, datée par `details`. */
function finalizeDuringWithdrawalMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("deadline", detailDate(error, WITHDRAWAL_DEADLINE_KEY)),
    O.apS("finalizable", detailDate(error, "first_finalizable_date")),
    O.match(
      () => "Le délai de rétractation du stagiaire court encore : le contrat se finalise une fois ce délai écoulé.",
      ({ deadline, finalizable }) =>
        `Le délai de rétractation du stagiaire court jusqu'au ${deadline} inclus : le contrat se finalise à partir du ${finalizable}. D'ici là, la rétractation reste enregistrable.`,
    ),
  )
}

/** Rétractation refusée selon le contrat : avenant, signature, clôture. */
function contractStatusNotWithdrawableMessage(error: unknown): string {
  const isAmendment = O.isSome(pipe(extractErrorDetailField(error, "contract_type"), O.filter((type) => type === "amendment")))
  const hasOwnPeriod = O.isSome(pipe(extractErrorDetailField(error, "opens_own_withdrawal"), O.filter((opens) => opens === true)))
  if (isAmendment && !hasOwnPeriod) {
    return "Cet avenant ne modifie aucune mention de l'article L6353-4 du Code du travail : il n'ouvre pas de nouveau délai de rétractation. Le délai et la rétractation restent ceux du contrat d'origine."
  }
  return pipe(
    extractErrorDetailField(error, "status"),
    O.filter((status): status is string => typeof status === "string" && UNSIGNED_CONTRACT_STATUSES.has(status)),
    O.match(
      () => CLOSED_CONTRACT_WITHDRAWAL_MESSAGE,
      () => "Le contrat n'est pas encore signé par toutes les parties : le délai de rétractation n'a pas commencé.",
    ),
  )
}

/** Lettre postée hors délai, datée par `details`. */
function withdrawalLetterLateMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("deadline", detailDate(error, WITHDRAWAL_DEADLINE_KEY)),
    O.apS("sent", detailDate(error, "letter_sent_on")),
    O.match(
      () => LETTER_LATE_FALLBACK,
      ({ deadline, sent }) =>
        `La lettre de rétractation a été envoyée le ${sent}, après le dernier jour du délai (${deadline}, inclus) : la rétractation ne s'enregistre pas.`,
    ),
  )
}

/** Refus servi à l'envoi d'une facture. */
function isInvoiceRefusal(error: unknown): boolean {
  return O.isSome(pipe(extractErrorDetailField(error, "operation"), O.filter((operation) => operation === "invoice")))
}

/** Envoi d'une facture pendant le délai, daté par `details`. */
function invoiceDuringWithdrawalMessage(error: unknown): string {
  return pipe(
    O.Do,
    O.apS("deadline", detailDate(error, WITHDRAWAL_DEADLINE_KEY)),
    O.apS("payable", detailDate(error, "first_payable_date")),
    O.match(
      () => `Envoi de la facture refusé : le délai de rétractation du stagiaire court encore, et aucune somme ne peut être exigée pendant ce délai ${WITHDRAWAL_PERIOD_LAW}.`,
      ({ deadline, payable }) =>
        `Envoi de la facture refusé : le délai de rétractation du stagiaire court jusqu'au ${deadline} inclus. Aucune somme ne peut être exigée pendant ce délai ${WITHDRAWAL_PERIOD_LAW}. Vous pourrez envoyer la facture à partir du ${payable}.`,
    ),
  )
}

/** Paiement ou facture après rétractation, chiffré par `details`. */
/** Motifs d'un contrat modifié refusé, `details.reason` du serveur. */
const AMENDED_CONTRACT_REASON_MESSAGES: Readonly<Record<string, string>> = {
  other_account:
    "Le contrat modifié appartient à un autre compte que l'avenant : choisissez un contrat du même compte. Un avenant ne modifie que le contrat de son propre client.",
  other_contact:
    "Le contrat modifié a pour stagiaire une autre personne que l'avenant : choisissez un contrat du même stagiaire. Un avenant ne modifie que le contrat de son propre client.",
  no_shared_party:
    "L'avenant ne désigne ni le compte ni le stagiaire du contrat qu'il modifie : choisissez d'abord le compte ou le contact de ce contrat.",
}

/** Contrat modifié refusé, précisé par `details.reason`. */
function amendedContractInvalidMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "reason"),
    O.filter((reason): reason is string => typeof reason === "string" && Object.hasOwn(AMENDED_CONTRACT_REASON_MESSAGES, reason)),
    O.map((reason) => AMENDED_CONTRACT_REASON_MESSAGES[reason]),
    O.alt(() => translateErrorCode("CONTRACT_AMENDED_CONTRACT_INVALID")),
    O.getOrElse(() => "Contrat modifié invalide."),
  )
}

function contractWithdrawnMessage(error: unknown): string {
  const isInvoice = isInvoiceRefusal(error)
  const act = isInvoice ? "Envoi de la facture" : "Paiement"
  const excess = isInvoice ? "cette facture dépasse ce montant" : "le cumul des paiements dépasserait ce montant"
  return pipe(
    detailDate(error, "withdrawn_on"),
    O.match(
      () => `${act} refusé : le stagiaire s'est rétracté.`,
      (day) =>
        pipe(
          detailNumber(error, "amount_due"),
          O.match(
            () => `${act} refusé : le stagiaire s'est rétracté le ${day}.`,
            (due) =>
              due > 0
                ? `${act} refusé : le stagiaire s'est rétracté le ${day} ; il ne doit plus que ${EURO.format(due)}, part de la formation suivie jusqu'à sa rétractation, due parce que sa demande expresse a été recueillie et qu'il a été informé (9° de l'article L. 221-5 ; article L. 221-25, deuxième alinéa, première phrase, du Code de la consommation), et ${excess}.`
                : `${act} refusé : le stagiaire s'est rétracté le ${day} et ne doit rien.`,
          ),
        ),
    ),
  )
}

/** Statuts d'un contrat avant sa signature complète. */
const UNSIGNED_CONTRACT_STATUSES = new Set(["draft", "pending_review", "sent", "partially_signed"])

/** Champ réservé à une action ; message propre à la résolution. */
function complaintStatusViaActionMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "field"),
    O.filter((field) => field === "resolution"),
    O.match(
      () => "Ce statut se pose par son bouton (accuser réception, résoudre, clôturer), qui en garde la date et l'historique.",
      () => "La résolution se saisit dans le dialogue « Résoudre » de la fiche, qui en garde la date et l'historique.",
    ),
  )
}

/** Action citée par la base de connaissances ou la veille. Chiffrée par `details.count`. */
function improvementActionReferencedMessage(error: unknown): string {
  const citingItemsPhrase = pipe(
    extractErrorDetailField(error, "count"),
    O.filter((count): count is number => typeof count === "number" && Number.isInteger(count) && count > 0),
    O.match(
      () => "des éléments",
      (count) => (count === 1 ? "un élément" : `${count} éléments`),
    ),
  )
  return `Cette action d'amélioration est citée par ${citingItemsPhrase} de la base de connaissances ou de la veille : elle reste au registre. Pour l'écarter, passez-la au statut « Annulée ».`
}

/**
 * Suppression d'une mission refusée : des fiches signées la portent.
 * Leur nombre vient de `details.staff_count`.
 * Absent hors de ce refus.
 */
function missionLockedMessage(error: unknown): O.Option<string> {
  return pipe(
    detailNumber(error, "staff_count"),
    O.filter((count) => Number.isInteger(count) && count > 0),
    O.map((count) =>
      count === 1
        ? "Cette mission figure sur une fiche de poste en signature ou signée : révoquez cette signature avant de la supprimer."
        : `Cette mission figure sur ${count} fiches de poste en signature ou signées : révoquez ces signatures avant de la supprimer.`,
    ),
  )
}

/** Nombre strictement positif porté par `details.count`. */
function detailCount(error: unknown): O.Option<number> {
  return pipe(
    extractErrorDetailField(error, "count"),
    O.filter((count): count is number => typeof count === "number" && Number.isInteger(count) && count > 0),
  )
}

/** Facture payée, chiffrée par `details.count`. */
function invoiceHasPaymentsMessage(error: unknown): string {
  return pipe(
    detailCount(error),
    O.match(
      () => "Des paiements sont enregistrés sur cette facture : elle ne se supprime pas, pour que ces paiements gardent leur facture.",
      (count) =>
        count === 1
          ? "Un paiement est enregistré sur cette facture : elle ne se supprime pas, pour que ce paiement garde sa facture."
          : `${count} paiements sont enregistrés sur cette facture : elle ne se supprime pas, pour que ces paiements gardent leur facture.`,
    ),
  )
}

/** Bloc noté par le jury, chiffré par `details.count`. */
function competencyBlockHasGradesMessage(error: unknown): string {
  return pipe(
    detailCount(error),
    O.match(
      () => "Des notes du jury portent sur ce bloc de compétences : il ne se supprime pas, sinon ces notes deviendraient des notes globales.",
      (count) =>
        count === 1
          ? "Une note du jury porte sur ce bloc de compétences : il ne se supprime pas, sinon cette note deviendrait une note globale."
          : `${count} notes du jury portent sur ce bloc de compétences : il ne se supprime pas, sinon ces notes deviendraient des notes globales.`,
    ),
  )
}

// Champs d'une fiche de connaissances avec un message propre.
const IMPROVEMENT_MISMATCH_FIELD_MESSAGES: Record<string, string> = {
  source_action_id: "L'action d'amélioration d'origine n'appartient pas à votre organisme : choisissez une action de votre registre.",
  category_id: "La catégorie ou le thème choisi n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste.",
  topic_id: "La catégorie ou le thème choisi n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste.",
}

/** Référence d'un autre organisme, précisée par `details.field`. */
function improvementReferenceMismatchMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "field"),
    O.chain((field) => (typeof field === "string" ? O.fromNullable(IMPROVEMENT_MISMATCH_FIELD_MESSAGES[field]) : O.none)),
    O.getOrElse(
      () => "Le type, le statut, la personne assignée ou l'origine choisie n'appartient pas à votre organisme : rechargez la page puis choisissez-en un dans la liste.",
    ),
  )
}

/** Plafond de pièces externes d'une inscription, chiffré quand le serveur le donne (`details.max`). */
function proofFileLimitMessage(error: unknown): string {
  const count = pipe(
    extractErrorDetailField(error, "max"),
    O.filter((max): max is number => typeof max === "number" && Number.isInteger(max) && max > 0),
    O.match(
      () => "",
      (max) => ` (${max} au maximum)`,
    ),
  )
  return `Cette inscription a atteint le plafond de pièces externes${count}. Supprimez une pièce devenue inutile avant d'en ajouter une autre.`
}

/**
 * Codes dont le serveur rédige lui-même le message en français, avec le
 * détail utile : borne réelle de la catégorie (413), types admis (415), cause
 * du refus d'export (429 : export en cours ou plafond horaire), plafond de
 * feuilles d'un dossier (422, chiffré), relevé trop lourd (422). Le message de
 * la table ne sert qu'à défaut de détail.
 */
const SERVER_DETAIL_CODES = new Set([
  // Refus de lecture : le serveur nomme le champ.
  "INVALID_REQUEST_BODY",
  "INVALID_PATH_PARAMETER",
  "INVALID_QUERY_PARAMETER",
  "REQUEST_BODY_TOO_LARGE",
  "UNSUPPORTED_CONTENT_TYPE",
  "RATE_LIMITED",
  "VALIDATION_ERROR",
  "REQUEST_VALIDATION_FAILED",
  "SURVEY_TEMPLATE_INVALID",
  "PROOF_FILE_TOO_LARGE",
  "UNSUPPORTED_FILE_TYPE",
  "RATE_LIMITED_BUNDLE",
  "TOO_MANY_ATTENDANCE_SHEETS",
  "ATTENDANCE_STATEMENT_TOO_LARGE",
  "EVALUATION_SCORE_INVALID",
  "EVALUATION_ANSWERS_INVALID",
  "EVALUATION_TEMPLATE_INVALID",
  "EVALUATION_QUESTION_INVALID",
  "EVALUATION_QUESTION_HAS_ANSWERS",
  "SURVEY_ANSWERS_INVALID",
  "SURVEY_QUESTION_HAS_ANSWERS",
  "SURVEY_QUESTION_HAS_DEPENDENTS",
  "SURVEY_TEMPLATE_IN_USE",
  "PROGRAM_NEEDS_TEMPLATE_INVALID",
  "SESSION_DOCUMENT_INVALID",
  // Marque blanche des PDF, signature du responsable, réouverture d'une feuille.
  "PDF_BRANDING_PLAN_REQUIRED",
  "PDF_BRANDING_CUSTOM_TEXT_TOO_LONG",
  "SIGNATURE_ASSET_INVALID",
  "SIGNATURE_ASSET_NOT_FOUND",
  "SIGNATURE_NOT_CONFIGURED",
  "ATTENDANCE_SHEET_NOT_FINALIZED",
  "ATTENDANCE_REOPEN_REASON_REQUIRED",
  "SIGNUP_ATTRIBUTION_INVALID",
  "CONTACT_EMAIL_IN_USE",
  "SURVEY_ASSIGNMENT_TARGET_INVALID",
  "SURVEY_ASSIGNMENT_TARGET_STAKEHOLDER_MISMATCH",
  "SURVEY_ASSIGNMENT_TEMPLATE_NOT_SENDABLE",
  "SURVEY_TEMPLATE_AUTO_SEND_RECIPIENT",
  "SURVEY_TEMPLATE_AUTO_SEND_TRIGGER_REQUIRED",
  "EVALUATION_TARGET_STAKEHOLDER_MISMATCH",
  "SURVEY_TEMPLATE_STAKEHOLDER_CATEGORY_FIXED",
  "TENANT_CLOSED",
  // Modèles de documents : le serveur nomme la partie fautive.
  "DOCUMENT_TEMPLATE_SYNTAX_INVALID",
  "DOCUMENT_TEMPLATE_HELPER_UNKNOWN",
  "DOCUMENT_TEMPLATE_RENDER_FAILED",
  "DOCUMENT_TEMPLATE_SERVES_CARD",
  "DOCUMENT_TEMPLATE_PARTIAL_FORBIDDEN",
  "DOCUMENT_TEMPLATE_NESTING_TOO_DEEP",
  "DOCUMENT_TEMPLATE_SOURCE_TOO_LONG",
  "DOCUMENT_TEMPLATE_RENDER_TOO_LARGE",
  "DOCUMENT_TEMPLATE_RENDER_BUDGET_EXCEEDED",
  // Modèles d'e-mails : la garde des sources nomme la construction refusée.
  "EMAIL_TEMPLATE_PARTIAL_FORBIDDEN",
  "EMAIL_TEMPLATE_NESTING_TOO_DEEP",
  "EMAIL_TEMPLATE_HELPER_UNKNOWN",
  "EMAIL_TEMPLATE_SOURCE_TOO_LONG",
  "EMAIL_TEMPLATE_RENDER_TOO_LARGE",
  "EMAIL_TEMPLATE_RENDER_BUDGET_EXCEEDED",
])

/**
 * `detail` du Problem JSON, quand il est renseigné.
 *
 * @example problemDetail({ response: { data: { detail: "Limite atteinte." } } }) // O.some("Limite atteinte.")
 */
export function problemDetail(error: unknown): O.Option<string> {
  return pipe(
    extractErrorBodyField(error, "detail"),
    O.filter((detail): detail is string => typeof detail === "string" && detail.trim().length > 0),
  )
}

/** Nom d'un module dans un droit affiché : « RH › Supprimer ». */
const PERMISSION_MODULE_RIGHT_LABELS: Record<PermissionModule, string> = {
  training: "Formation",
  commercial: "Commercial",
  crm: "CRM",
  hr: "RH",
  qualiopi: "Qualiopi",
  jury: "Jurys",
  documents: "Documents",
  templates: "Modèles",
  messaging: "Messagerie",
  admin: "Administration",
  reports: "Rapports",
  settings: "Paramètres",
}

const PERMISSION_OPERATION_LABELS: Record<PermissionOperation, string> = {
  read: "Lire",
  write: "Modifier",
  delete: "Supprimer",
  export: "Exporter",
  transition: "Transition",
  generate: "Générer",
}

/**
 * Droit nommé comme dans l'application.
 * @example requiredRightLabel("crm", "delete") // "CRM › Supprimer"
 */
export function requiredRightLabel(module: PermissionModule, operation: PermissionOperation): string {
  return `${PERMISSION_MODULE_RIGHT_LABELS[module]} › ${PERMISSION_OPERATION_LABELS[operation]}`
}

// Refus de `require_permission` : « Permission requise : hr.delete ».
const PERMISSION_DENIED_DETAIL = /^Permission requise : ([a-z_]+)\.([a-z_]+)$/

function ownLabel(labels: Readonly<Record<string, string>>, key: string): O.Option<string> {
  return Object.hasOwn(labels, key) ? O.some(labels[key]) : O.none
}

/** Droit manquant, nommé comme dans l'application. */
function permissionDeniedMessage(error: unknown): O.Option<string> {
  return pipe(
    problemDetail(error),
    O.chain((detail) => O.fromNullable(PERMISSION_DENIED_DETAIL.exec(detail))),
    O.map(([, module, operation]) =>
      pipe(
        ownLabel(PERMISSION_MODULE_RIGHT_LABELS, module),
        O.chain((moduleLabel) =>
          pipe(
            ownLabel(PERMISSION_OPERATION_LABELS, operation),
            O.map((operationLabel) => `${moduleLabel} › ${operationLabel}`),
          ),
        ),
        O.match(
          () => "Votre rôle ne vous donne pas le droit nécessaire à cette action.",
          (right) => `Votre rôle ne vous donne pas le droit nécessaire à cette action (${right}).`,
        ),
      ),
    ),
  )
}

/** Exports réservés à Qualiopi › Lire, nommés en clair. */
const QUALIOPI_EXPORT_TYPE_NAMES: Readonly<Record<string, string>> = {
  complaints: "L'export des réclamations",
  evaluations: "L'export des évaluations",
  survey_sends: "L'export des envois d'enquêtes",
}

/** Export refusé sans Qualiopi › Lire, nommé par `details.export_type`. */
function exportTypeForbiddenMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "export_type"),
    O.filter((type): type is string => typeof type === "string"),
    O.chain((type) => ownLabel(QUALIOPI_EXPORT_TYPE_NAMES, type)),
    O.match(
      () => "Cet export exige le droit Qualiopi › Lire : demandez-le à un administrateur.",
      (name) => `${name} exige le droit Qualiopi › Lire : demandez-le à un administrateur.`,
    ),
  )
}

/**
 * Translate a backend error into a user-friendly French message.
 * Falls back to the raw detail if no translation is found,
 * prefixed with a generic header if the message appears to be in English.
 */
/**
 * Traduction d'un code d'erreur seul (sans réponse HTTP autour), `none` si
 * le code n'a pas de traduction.
 */
export function translateErrorCode(code: string): O.Option<string> {
  const lowerCode = code.toLowerCase()
  return pipe(
    O.fromNullable(ERROR_TRANSLATIONS.find((entry) => entry.pattern === lowerCode)),
    O.map((entry) => entry.message),
  )
}

/** Variable sans valeur, nommée quand le serveur la donne. */
function templateVariableMissingMessage(error: unknown): string {
  return pipe(
    extractErrorDetailField(error, "variable"),
    O.filter((variable): variable is string => typeof variable === "string" && variable.length > 0),
    O.match(
      () => TEMPLATE_VARIABLE_MISSING_MESSAGE,
      (variable) =>
        `Le modèle utilise l'information « ${variable} », qui n'est pas renseignée pour cet élément : vérifiez son écriture dans le modèle, ou complétez la fiche concernée, puis relancez la génération.`,
    ),
  )
}

export function translateError(error: unknown): string {
  if (error instanceof ImportRefusedError) return error.message
  const code = extractErrorCode(error)
  if (code === "DOCUMENT_TEMPLATE_VARIABLE_MISSING") {
    return templateVariableMissingMessage(error)
  }
  if (code === "PROOF_FILE_LIMIT_REACHED") {
    return proofFileLimitMessage(error)
  }
  if (code === "EXPORT_TYPE_FORBIDDEN") {
    return exportTypeForbiddenMessage(error)
  }
  if (code === "IMPROVEMENT_ACTION_REFERENCED") {
    return improvementActionReferencedMessage(error)
  }
  if (code === "INVOICE_HAS_PAYMENTS") {
    return invoiceHasPaymentsMessage(error)
  }
  if (code === "COMPETENCY_BLOCK_HAS_GRADES") {
    return competencyBlockHasGradesMessage(error)
  }
  if (code === "IMPROVEMENT_REFERENCE_MISMATCH") {
    return improvementReferenceMismatchMessage(error)
  }
  if (code === "ATTENDANCE_SIGN_WRONG_DAY") {
    return signWrongDayMessage(error)
  }
  if (code === "JOB_DESCRIPTION_LOCKED") {
    const missionLocked = missionLockedMessage(error)
    if (O.isSome(missionLocked)) return missionLocked.value
  }
  if (code === "JOB_DESCRIPTION_OVERRIDE_NOT_YET_AVAILABLE") {
    // Aucun fuseau d'organisme ici.
    // La date d'ouverture reste au menu Actions.
    return "La signature à la place du manager s'ouvre 7 jours après la demande de signature. Sa date d'ouverture figure dans le menu Actions de la fiche du collaborateur."
  }
  if (code === "ATTENDANCE_RATE_TOO_LOW") {
    return registrationAttendanceRateMessage(error)
  }
  if (code === "REGISTRATION_TRANSITION_NOT_ALLOWED") {
    return registrationTransitionMessage(error)
  }
  if (code === "CONTRACT_PARTY_NOT_SIGNED") {
    return contractPartyNotSignedMessage(error)
  }
  if (code === "COMPLAINT_STATUS_VIA_ACTION") {
    return complaintStatusViaActionMessage(error)
  }
  if (code === "SESSION_ACTUAL_END_OUT_OF_RANGE") {
    return sessionActualEndRangeMessage(error)
  }
  if (code === "INSTRUCTOR_RATE_ABOVE_CAP") {
    return instructorRateAboveCapMessage(error)
  }
  if (code === "SESSION_DATES_EXCLUDE_FINALIZED_SHEETS") {
    return sessionDatesExcludeSheetsMessage(error)
  }
  if (code === "SESSION_DATES_EXCLUDE_SLOTS") {
    return sessionDatesExcludeSlotsMessage(error)
  }
  if (code === "ATTENDANCE_SESSION_STATUS_LOCKED") {
    return sessionStatusLockedMessage(error)
  }
  if (code === "ATTENDANCE_SHEET_OUTSIDE_SESSION_DATES") {
    return sheetOutsideSessionDatesMessage(error)
  }
  if (code === "SESSION_ACTUAL_END_HAS_RECORDED_SHEETS") {
    return actualEndRecordedSheetsMessage(error)
  }
  if (code === "SESSION_HAS_UNFINALIZED_SHEETS") {
    return unfinalizedSheetsMessage(error)
  }
  if (code === "SESSION_HAS_REGISTRATIONS") {
    return sessionHasRegistrationsMessage(error)
  }
  if (code === "ATTENDANCE_SHEET_AFTER_ACTUAL_END" || code === "ATTENDANCE_SHEET_IN_FUTURE") {
    return attendanceSheetDateMessage(error, code)
  }
  if (code === "CONTRACT_WITHDRAWAL_PERIOD_RUNNING" && O.isSome(extractErrorDetailField(error, "first_finalizable_date"))) {
    return finalizeDuringWithdrawalMessage(error)
  }
  if (code === "CONTRACT_STATUS_NOT_WITHDRAWABLE") {
    return contractStatusNotWithdrawableMessage(error)
  }
  if (code === "CONTRACT_WITHDRAWAL_LETTER_LATE") {
    return withdrawalLetterLateMessage(error)
  }
  if (code === "CONTRACT_WITHDRAWAL_PERIOD_RUNNING" && isInvoiceRefusal(error)) {
    return invoiceDuringWithdrawalMessage(error)
  }
  if (code === "CONTRACT_WITHDRAWAL_PERIOD_RUNNING") {
    return withdrawalPeriodRunningMessage(error)
  }
  if (code === "CONTRACT_DEPOSIT_CAP_EXCEEDED") {
    return depositCapExceededMessage(error)
  }
  if (code === "RETENTION_OUT_OF_BOUNDS") {
    return retentionBoundsMessage(error)
  }
  if (code === "TENANT_TERMINATION_DATE_INVALID") {
    return terminationDateMessage(error)
  }
  if (code === "CONTRACT_WITHDRAWN") {
    return contractWithdrawnMessage(error)
  }
  if (code === "CONTRACT_AMENDED_CONTRACT_INVALID") {
    return amendedContractInvalidMessage(error)
  }
  if (code === "SLOT_DATE_OUT_OF_SESSION") {
    return slotDateOutOfSessionMessage(error)
  }
  const permissionDenied = permissionDeniedMessage(error)
  if (O.isSome(permissionDenied)) return permissionDenied.value
  const serverDetail = pipe(
    O.fromNullable(code),
    O.filter((known) => SERVER_DETAIL_CODES.has(known)),
    O.chain(() => problemDetail(error)),
  )
  if (O.isSome(serverDetail)) return serverDetail.value
  if (code) {
    const lowerCode = code.toLowerCase()
    for (const entry of ERROR_TRANSLATIONS) {
      if (lowerCode === entry.pattern) {
        return entry.message
      }
    }
  }

  const detail = extractErrorDetail(error)

  // Check partial matches (case-insensitive)
  const lowerDetail = detail.toLowerCase()
  for (const entry of ERROR_TRANSLATIONS) {
    if (lowerDetail.includes(entry.pattern)) {
      return entry.message
    }
  }

  // If the detail already contains French accented characters, return as-is
  if (/[àâéèêëîïôùûüç]/i.test(detail)) {
    return detail
  }

  // Generic fallback wrapping the raw detail
  return `Une erreur est survenue : ${detail}`
}

/**
 * Échec du chargement de la fiche d'une inscription.
 * Message selon la cause, jamais le texte brut d'axios.
 *
 * @example
 * registrationLoadErrorMessage(error) // "Inscription introuvable ou inaccessible." sur un 404
 */
export function registrationLoadErrorMessage(error: unknown): string {
  const status = extractErrorStatus(error)
  if (status === 404) return "Inscription introuvable ou inaccessible."
  if (status === 403) return "Vous n'avez pas accès à cette inscription (droit requis : Formation › Lire)."
  return "L'inscription n'a pas pu être chargée."
}

/**
 * Message d'échec du chargement de la fiche d'un parcours d'inscription.
 *
 * @example
 * enrollmentLoadErrorMessage(error) // "Parcours introuvable ou inaccessible." sur un 404
 */
export function enrollmentLoadErrorMessage(error: unknown): string {
  const status = extractErrorStatus(error)
  if (status === 404) return "Parcours introuvable ou inaccessible."
  if (status === 403) return "Vous n'avez pas accès à ce parcours (droit requis : Formation › Lire)."
  return "Le parcours n'a pas pu être chargé."
}

/**
 * Convenience re-export: extracts and translates the error message,
 * falling back to the provided default when the error is empty/undefined.
 */
export function getErrorMessage(error: unknown, defaultMessage: string = "Une erreur est survenue"): string {
  const detail = extractErrorDetail(error)
  if (!detail || detail === "undefined" || detail === "[object Object]") {
    return defaultMessage
  }
  return translateError(error)
}

/**
 * Structured payload exposed when the backend rejects a self-registration
 * with `INVITATION_PENDING` (409). The frontend uses this to render a
 * targeted banner pointing the user to their mailbox / admin instead of
 * the generic "registration failed" message.
 */
export interface InvitationPendingData {
  invitation_id: string
  tenant_name: string
  tenant_slug: string
}

export function extractInvitationPending(error: unknown): InvitationPendingData | null {
  if (extractErrorCode(error) !== "INVITATION_PENDING") {
    return null
  }
  const data = (error as { response?: { data?: unknown } }).response?.data as
    | { invitation_id?: unknown; tenant_name?: unknown; tenant_slug?: unknown }
    | undefined
  if (
    typeof data?.invitation_id === "string"
    && typeof data?.tenant_name === "string"
    && typeof data?.tenant_slug === "string"
  ) {
    return {
      invitation_id: data.invitation_id,
      tenant_name: data.tenant_name,
      tenant_slug: data.tenant_slug,
    }
  }
  return null
}
